import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router'

import type { HttpClient } from '@molecule/app-http'
import type { TranslateFunction } from '@molecule/app-i18n'
import type { IconName } from '@molecule/app-icons'
import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button, Flex, Switch, Tooltip } from '@molecule/app-ui-react'

import { useIsMobile, useMediaQuery } from '../hooks/useMediaQuery.js'
import { emitNotification } from '../notifications.js'
import { type EnvSaveResponse, notifyEnvSaveOutcome } from '../utilities/env-save-notice.js'
import { errorKeyOf, isInsufficientRoleError, requiresSignup } from '../utilities/ops-utilities.js'
import { onResourceInvalidated } from '../utilities/resource-invalidation.js'
import { SERVICE_CATALOG, type ServiceCatalogEntry } from '../utilities/services-catalog.js'
import {
  billingFrame,
  deleteSecretAction,
  deriveServiceState,
  disableBrokerAction,
  enableBrokerAction,
  ENV_SCOPES,
  type EnvScope,
  type LiveServiceState,
  loadServicesState,
  putSecretAction,
  resolveSecretsForScope,
  runVaultAction,
  type ServicesBilling,
  type ServicesStateResponse,
  type ServiceState,
  splitCatalogByPresence,
  statusChip,
  type VaultResponse,
} from '../utilities/services-utilities.js'
import { formatCentsCompact } from '../utilities/usage-billing-utilities.js'
import { confirmDialog } from './AppDialogs.js'
import { EnvVarEditor } from './EnvVarEditor.js'
import { Icon } from './Icon.js'
import { MoleculeSpinner } from './MoleculeSpinner.js'
import { usePanelModalTabs } from './PanelModal.js'
import { MutedNote, PanelNotice } from './PanelNotice.js'

/**
 * The unified Environment view — ONE scrollable, sectioned surface replacing
 * the old Setup · Variables · Services tabs:
 *
 *  1. **Needs attention** — everything the user has to act on, and the only
 *     section that claims so: services and packages still missing required
 *     keys (HOISTED out of "In your app" — a "2 keys needed" chip halfway down
 *     a long list is easy to scroll past), then the detected env vars
 *     (`GET /projects/:id/env/detected`) nothing above already asks for. Below
 *     it, "Detected in your app" plus two collapsed explanatory groups
 *     ("Optional", "Set by molecule") account for every other var found.
 *  2. **In your app** — one collapsible row per service category the app
 *     actually uses (live `GET /projects/:id/services` presence), MERGED with
 *     the setup integrations (`GET /projects/:id/setup`) whose
 *     `serviceCategory` matches; plus one row per credentialed integration
 *     with no service category (database, push-notifications, …) — minus
 *     whatever section 1 hoisted.
 *  3. **Network access** — the project's sandbox egress allowlist
 *     ({@link NetworkAccessSection}): what the app is allowed to reach, and the
 *     one place to add a host it can't.
 *  4. **More services** — the rest of the curated catalog as collapsible rows,
 *     so users can see what's available (the discovery surface).
 *  5. **Custom environment variables** — one collapsible row hosting the raw
 *     env-var editor for the user's own config.
 *
 * A single hand-rolled accordion (one `useState<string|null>` hoisted here)
 * keeps exactly ONE row open across the whole view, so the collapsed summary
 * rows read as a quick "what do I have configured" overview.
 *
 * Credential dedup rule for merged rows: a nested setup secret's env key
 * renders ONCE via the rich setup rendering (instructions, broker notes,
 * masked values); the services-side BYO inputs render only for env keys NOT
 * already covered by a nested setup secret — the richest rendering wins and
 * the same key never appears twice.
 */

// ── GET /projects/:id/setup types ────────────────────────────────────────────

/** Per-secret setup state from GET /projects/:id/setup. */
export interface SetupSecret {
  name: string
  required: boolean
  label?: string
  setupUrl?: string
  instructions?: string
  example?: string
  status: 'set' | 'provisioned' | 'auto' | 'missing'
  masked?: string
  source?: string
  /**
   * Where a vault-held value comes from — `'broker'` means molecule.dev's
   * hosted backend provides it (nothing for the user to do).
   */
  vaultSource?: 'byo' | 'broker' | 'managed'
}

/** One installed integration from GET /projects/:id/setup. */
export interface SetupIntegration {
  package: string
  label?: string
  category?: string
  description?: string
  /**
   * The service category this integration belongs to (a services-catalog key
   * like 'ai' | 'payments' | 'oauth'), when one maps — nests the package's
   * secrets under its service row in section 1.
   */
  serviceCategory?: string
  secrets: SetupSecret[]
  needsSetup: boolean
  /**
   * Packages absorbed into this row by {@link dedupeIntegrations} — they
   * declared the IDENTICAL secret set, so rendering them separately would
   * show the same keys twice (e.g. the JWT signing provider and the
   * Authentication resource both declare JWT_PRIVATE_KEY/JWT_PUBLIC_KEY).
   */
  absorbedPackages?: string[]
}

/**
 * Merge integrations whose secret-NAME sets are IDENTICAL into one row — two
 * registry packages can each declare the same generated keys (both
 * declarations are load-bearing for scaffold env generation, so the registry
 * keeps them; the view must not render the same keys twice). Only exact set
 * equality merges — a subset stays its own row (a distinct integration's
 * identity is never hidden behind a bigger one).
 *
 * Survivor preference: the app-level resource package
 * (`@molecule/api-resource-*`) wins over low-level providers/utilities — the
 * user thinks "Authentication", not "jsonwebtoken" — then first
 * alphabetically for determinism. Absorbed package names are carried on the
 * survivor (`absorbedPackages`) and shown in the row's mono subtitle.
 *
 * @param integrations - The setup integrations as fetched.
 * @returns The deduplicated list, original order preserved by survivor.
 */
export function dedupeIntegrations(integrations: SetupIntegration[]): SetupIntegration[] {
  const setKey = (integration: SetupIntegration): string =>
    // A secret-less integration never merges (defensive — the API omits those).
    integration.secrets.length === 0
      ? `empty:${integration.package}`
      : integration.secrets
          .map((s) => s.name)
          .sort()
          .join('|')
  const preference = (a: SetupIntegration, b: SetupIntegration): SetupIntegration => {
    const aResource = a.package.startsWith('@molecule/api-resource-')
    const bResource = b.package.startsWith('@molecule/api-resource-')
    if (aResource !== bResource) return aResource ? a : b
    return a.package <= b.package ? a : b
  }

  const survivorByKey = new Map<string, SetupIntegration>()
  for (const integration of integrations) {
    const key = setKey(integration)
    const current = survivorByKey.get(key)
    survivorByKey.set(key, current ? preference(current, integration) : integration)
  }

  const out: SetupIntegration[] = []
  const emitted = new Set<string>()
  for (const integration of integrations) {
    const key = setKey(integration)
    if (emitted.has(key)) continue
    emitted.add(key)
    const survivor = survivorByKey.get(key) as SetupIntegration
    const absorbed = integrations
      .filter((i) => i !== survivor && setKey(i) === key)
      .map((i) => i.package)
    out.push(absorbed.length > 0 ? { ...survivor, absorbedPackages: absorbed } : survivor)
  }
  return out
}

/**
 * True when molecule (not the user) provides this secret — provisioned by the
 * platform, auto-generated at scaffold time, supplied by the hosted backend
 * (broker), or held as a managed vault row (molecule's managed storage).
 * Managed secrets have nothing for the user to do, so they never earn a
 * per-secret row.
 * @param secret - The secret's setup state.
 * @returns Whether molecule provides the value.
 */
export function isManagedSecret(secret: SetupSecret): boolean {
  return (
    secret.vaultSource === 'broker' ||
    secret.vaultSource === 'managed' ||
    secret.status === 'provisioned' ||
    secret.status === 'auto'
  )
}

/**
 * True when the USER set this secret — a value they saved themselves (vault
 * `byo` row or plaintext config), never one molecule supplied (`broker` rows
 * come from the hosted backend, `managed` rows from molecule's managed
 * storage). The one "Your keys" predicate shared by every chip and switch
 * derivation across the Environment and Storage panels.
 * @param secret - The secret's setup state.
 * @returns Whether the user (not molecule) provided the value.
 */
export function isUserSetSecret(secret: SetupSecret): boolean {
  return (
    secret.status === 'set' && secret.vaultSource !== 'broker' && secret.vaultSource !== 'managed'
  )
}

/**
 * True when the list is non-empty and EVERY secret is molecule-managed — the
 * row shows a single "Managed by molecule" line instead of per-secret rows
 * (there is nothing to configure).
 * @param secrets - The secrets to test.
 * @returns Whether the whole set is molecule-managed.
 */
export function allSecretsManaged(secrets: SetupSecret[]): boolean {
  return secrets.length > 0 && secrets.every(isManagedSecret)
}

/** The GET /projects/:id/setup response envelope. */
export interface SetupResponse {
  integrations: SetupIntegration[]
  needsSetupCount: number
  missingRequired: number
  /**
   * What THIS deployment can provide, independent of the project. Panels use
   * it to describe molecule's defaults by FEATURE DETECTION rather than by
   * assertion — the Storage panel promised a managed object-storage default on
   * a deployment that provisions none. Optional: an older server omits it, and
   * the honest reading of "unknown" is "do not claim it".
   */
  platform?: {
    /** Whether molecule provisions S3-compatible buckets for projects here. */
    managedObjectStorage?: boolean
  }
}

interface EnvVarsResponse {
  envVars: Record<string, string>
  count: number
}

// ── GET /projects/:id/env/detected types (E-03) ──────────────────────────────

/** Why the platform provides a var — the API sends a kind, this file the words. */
type PlatformEnvKind = 'generated' | 'egress' | 'dev-server' | 'database' | 'sidecar' | 'container'

/**
 * Which part of the panel a var belongs in. The API decides this, not the panel:
 * the toolbar badge counts the same field, so the two cannot disagree about what
 * needs the user's attention.
 */
type DetectedEnvGroup = 'attention' | 'optional' | 'platform' | 'set'

/**
 * One env var actually referenced by the app's code (`process.env.X` /
 * `import.meta.env.X`) or declared by an installed integration's registry
 * entry — from `GET /projects/:id/env/detected`.
 */
interface DetectedEnvVar {
  key: string
  /** `path:line` references in app code (empty for registry-only declarations). */
  files: string[]
  set: boolean
  source: 'vault' | 'config' | 'registry' | 'platform' | null
  group: DetectedEnvGroup
  /** Set when the platform provides the value, and it is known why. */
  platformKind?: PlatformEnvKind
  /** Every runtime read falls back to something, so the app runs without it. */
  hasDefault?: boolean
  /** The literal the code falls back to. */
  defaultValue?: string
  /** A trailing `//` comment where the var is read — the author's own hint. */
  comment?: string
  /** The declaring package's answer to whether it has to be filled in. */
  required?: boolean
  label?: string
  instructions?: string
  setupUrl?: string
}

/** The GET /projects/:id/env/detected response envelope. */
interface DetectedEnvResponse {
  vars: DetectedEnvVar[]
  missingCount: number
  /** Tooling-only keys the report dropped, so the panel can say so in one line. */
  hiddenCount?: number
}

// ── Icons ────────────────────────────────────────────────────────────────────

/**
 * Category → bonded icon-set glyph. Complete and DISTINCT: every known service
 * or package category gets its own glyph (`getIcon` throws on unknown names, so
 * every name here is verified against the bonded set + workspace extras). The
 * fallback is `gear` — deliberately NOT `server`, which is the Environment
 * modal's own header/toolbar glyph; falling back to it made every unmapped
 * category render the same icon as the modal itself.
 */
const CATEGORY_ICONS: Record<string, IconName> = {
  ai: 'ai',
  embeddings: 'search',
  'image-generation': 'image',
  speech: 'microphone',
  translation: 'globe',
  maps: 'pin',
  payments: 'credit-card',
  oauth: 'key',
  auth: 'key',
  kyc: 'check-circle',
  esign: 'pencil',
  email: 'mail',
  emails: 'mail',
  'emails-inbound': 'mail',
  sms: 'chat',
  'push-notifications': 'bell',
  notifications: 'bell',
  database: 'database',
  uploads: 'upload',
  storage: 'folder',
  webhook: 'link',
  search: 'search',
  analytics: 'table',
  calendar: 'calendar',
  ocr: 'eye',
  'web-search': 'browser',
  // Every category the services catalog names has its own glyph: rows that
  // fell back to the gear read as one another (environment-tabs-consistency).
  'ai-decisions': 'lightbulb',
  'ai-summarization': 'sort-desc',
  'web-content': 'link-external',
  image: 'maximize',
  moderation: 'exclamation-triangle',
  pdf: 'file',
  screenshots: 'camera',
}

/**
 * Resolves the icon-set glyph for a category.
 * @param category - The service/package category.
 * @returns A glyph name guaranteed to exist in the bonded set.
 */
const iconFor = (category?: string): IconName => CATEGORY_ICONS[category ?? ''] ?? 'gear'

/**
 * Integration categories whose outbound traffic molecule CAPTURES in
 * development (surfaced in the Activity panel) — their rows carry a notice
 * that keys are only needed for real delivery in production.
 */
const CAPTURED_CATEGORIES: ReadonlySet<string> = new Set([
  'email',
  'emails',
  'emails-inbound',
  'sms',
])

// ── Chips ────────────────────────────────────────────────────────────────────

/** Semantic tone for a status chip (colors from ClassMap theme tokens). */
export type ChipTone = 'broker' | 'byo' | 'warning' | 'managed' | 'configured' | 'none'

/**
 * Inline status chip. Color comes from a ClassMap theme token (never a
 * hardcoded hex), with a matching subtle tint mixed from the same theme var so
 * the chip tracks the active theme — the one chip treatment for every row.
 * Shared with the Storage panel (the same chip idiom on its rows).
 * @param root0 - Component props.
 * @param root0.tone - Semantic tone.
 * @param root0.molId - Automation id.
 * @param root0.children - Chip label.
 * @returns The rendered chip.
 */
export function Chip({
  tone,
  molId,
  children,
}: {
  tone: ChipTone
  molId: string
  children: ReactNode
}) {
  const cm = getClassMap()
  const toneClass =
    tone === 'broker'
      ? cm.textSuccess
      : tone === 'configured'
        ? cm.textSuccess
        : tone === 'byo'
          ? cm.textPrimary
          : tone === 'warning'
            ? cm.textWarning
            : cm.textMuted
  const toneBg =
    tone === 'broker' || tone === 'configured'
      ? 'color-mix(in srgb, var(--color-success) 15%, transparent)'
      : tone === 'byo'
        ? 'color-mix(in srgb, var(--color-primary) 15%, transparent)'
        : tone === 'warning'
          ? 'color-mix(in srgb, var(--color-warning) 14%, transparent)'
          : 'color-mix(in srgb, var(--color-foreground-secondary) 12%, transparent)'
  return (
    <span
      data-mol-id={molId}
      className={toneClass}
      style={{
        flexShrink: 0,
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        whiteSpace: 'nowrap',
        padding: '2px 7px',
        borderRadius: 999,
        background: toneBg,
      }}
    >
      {children}
    </span>
  )
}

// ── Row shell (single-open accordion row) ────────────────────────────────────

/**
 * One collapsible row: full-width header button (leading category icon, label,
 * optional muted mono package name, status chip, chevron) + an expanded body.
 * Shared with the Storage panel (the same single-open accordion row idiom).
 * @param root0 - Component props.
 * @param root0.molId - Automation id for the row container.
 * @param root0.icon - Bonded icon-set glyph name for the leading icon.
 * @param root0.label - Row label.
 * @param root0.packageName - Optional package name (package-only rows).
 * @param root0.chip - The status chip node.
 * @param root0.expanded - Whether this row is the open one.
 * @param root0.onToggle - Toggles this row open/closed.
 * @param root0.children - Expanded body content.
 * @returns The rendered row.
 */
export function EnvRow({
  molId,
  icon,
  label,
  packageName,
  chip,
  expanded,
  onToggle,
  children,
}: {
  molId: string
  icon: IconName
  label: string
  packageName?: string
  chip: ReactNode
  expanded: boolean
  onToggle: () => void
  children: ReactNode
}) {
  const cm = getClassMap()
  // Phones drop the muted @molecule/* package name — there's no room beside
  // the label + chip, and it's developer metadata, not the row's identity.
  const isMobile = useIsMobile()
  return (
    <div
      data-mol-id={molId}
      style={{
        marginBottom: 8,
        borderRadius: 6,
        border: '1px solid var(--color-border)',
        overflow: 'hidden',
      }}
    >
      {/* mol-bespoke-button: a collapsible SECTION HEADER, not a CTA — the
          full-width header bar of the card it opens, flush inside the card's own
          rounded border. A button box here would draw a second frame. */}
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '8px 12px',
          background: 'var(--color-surface)',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--mol-color-text, #e0e0e0)',
          fontSize: 13,
          textAlign: 'left',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {/* Themed SVG glyph from the IDE icon set — never emoji. */}
          <span aria-hidden="true" style={{ display: 'inline-flex', flexShrink: 0 }}>
            <Icon name={icon} size={16} />
          </span>
          {/* Row names never wrap ("File storage" must stay one line on
              mobile) — a genuinely-too-narrow row ellipsizes instead. */}
          <span
            style={{
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {label}
          </span>
          {packageName && !isMobile && (
            <span
              className={cm.textMuted}
              style={{
                fontSize: 10,
                fontFamily: 'monospace',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {packageName}
            </span>
          )}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {chip}
          <span aria-hidden="true" style={{ display: 'inline-flex' }}>
            <Icon
              name={expanded ? 'chevron-up' : 'chevron-down'}
              size={14}
              className={cm.textSubtle}
            />
          </span>
        </span>
      </button>
      {expanded && (
        <div style={{ padding: 12, borderTop: '1px solid var(--color-border)' }}>{children}</div>
      )}
    </div>
  )
}

/**
 * Uppercase section heading (the "In your app" / "More services" idiom).
 * Shared with the Storage panel (its "In your app" / "More storage types").
 * @param root0 - Component props.
 * @param root0.molId - Automation id.
 * @param root0.first - Whether this is the first section (no top margin).
 * @param root0.children - Heading text.
 * @returns The rendered heading.
 */
export function SectionHeading({
  molId,
  first,
  children,
}: {
  molId: string
  first?: boolean
  children: ReactNode
}) {
  return (
    <h4
      data-mol-id={molId}
      style={{
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: 'var(--mol-color-text-secondary, #aaaaaa)',
        margin: first ? '0 0 8px' : '16px 0 8px',
      }}
    >
      {children}
    </h4>
  )
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Heuristic: env-var keys whose VALUE should be hidden behind a password field.
 * Keys containing words commonly associated with credentials / secrets get
 * `type="password"`; everything else renders as plain text so non-sensitive
 * config values (URLs, hostnames, feature flags, …) stay readable.
 */
const SENSITIVE_KEY_WORDS = [
  'SECRET',
  'TOKEN',
  'PASSWORD',
  'PASS',
  'PRIVATE',
  'CREDENTIAL',
  'CERT',
  'SALT',
  'SIGNING',
  'JWT',
  'ENCRYPTION',
  'DECRYPT',
  'CIPHER',
  'PEPPER',
]

function isSensitiveKey(key: string): boolean {
  const upper = key.toUpperCase()
  return SENSITIVE_KEY_WORDS.some((word) => upper.includes(word))
}

// ── Setup credential rows (the rich per-secret rendering) ────────────────────

/**
 * One per-secret credential row: fixed-width monospace label (check icon when
 * set), broker/managed/provisioned/auto notes, masked value, password input
 * for missing keys, and get-a-key instructions. With `override` (the "Use
 * your own keys" switch is ON), molecule-supplied managed/provisioned rows
 * additionally grow a password input beneath their note — saving a typed key
 * via the normal flow overwrites the managed vault row (the BYO takeover).
 * @param root0 - Component props.
 * @param root0.secret - The secret's setup state.
 * @param root0.value - The current draft input value.
 * @param root0.onChange - Draft change handler.
 * @param root0.override - Offer an own-key input on managed/provisioned rows.
 * @param root0.locked - Credential writes are impossible for this role (guest
 *   or non-admin), so the inputs render disabled instead of accepting a key
 *   that the save will only bounce.
 * @returns The rendered secret row.
 */
function SecretRow({
  secret,
  value,
  onChange,
  override,
  locked,
}: {
  secret: SetupSecret
  value: string
  onChange: (value: string) => void
  override?: boolean
  locked?: boolean
}) {
  const cm = getClassMap()
  const { t } = useTranslation()
  // Phones: the fixed 200px label column + 206px indents don't fit 390px —
  // the label sits ABOVE the input and indents collapse. 16px input font stops
  // iOS Safari's zoom-on-focus (it zooms any focusable field below 16px).
  const isMobile = useIsMobile()
  const coarse = useMediaQuery('(pointer: coarse)')
  const inputFontSize = isMobile || coarse ? 16 : 12
  const indent = isMobile ? 12 : 206

  const note =
    secret.vaultSource === 'managed'
      ? t('workspace.setup.managedProvided', undefined, {
          defaultValue: "Provided by Molecule's managed storage — nothing to do.",
        })
      : secret.vaultSource === 'broker'
        ? t('workspace.setup.brokerProvided', undefined, {
            defaultValue: "Provided by Molecule's hosted backend — nothing to do.",
          })
        : secret.status === 'provisioned'
          ? t('workspace.setup.provisioned', undefined, {
              defaultValue: 'Provisioned automatically by Molecule — nothing to do.',
            })
          : secret.status === 'auto'
            ? t('workspace.setup.autoGenerated', undefined, {
                defaultValue: 'Generated automatically for this app — nothing to do.',
              })
            : null

  return (
    <div data-mol-id={`setup-var-${secret.name}`}>
      {/* flexWrap never engages on desktop (the 200px label + flexible value
          always fit); on phones the full-width label pushes the value below. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <label
          className={secret.status === 'set' ? cm.textSuccess : cm.textMuted}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            width: isMobile ? '100%' : 200,
            flexShrink: 0,
            fontSize: 11,
            fontFamily: 'monospace',
          }}
        >
          {/* Configured vars lead with the themed check <Icon>. */}
          {secret.status === 'set' && <Icon name="check" size={12} />}
          {secret.name}
          {!secret.required && (
            <span className={cm.textMuted} style={{ fontFamily: 'inherit' }}>
              {t('workspace.setup.optional', undefined, { defaultValue: '(optional)' })}
            </span>
          )}
        </label>
        {note ? (
          <span
            className={cm.textMuted}
            style={{
              fontSize: 11,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              minWidth: 0,
            }}
          >
            {/* Broker/managed-provided: masked value + a small "molecule" tag
                (the value comes from molecule's hosted backend or its managed
                storage), matching the chip idiom. */}
            {(secret.vaultSource === 'broker' || secret.vaultSource === 'managed') && (
              <>
                {secret.masked && <span style={{ fontFamily: 'monospace' }}>{secret.masked}</span>}
                <span
                  data-mol-id={
                    secret.vaultSource === 'managed'
                      ? `setup-managed-tag-${secret.name}`
                      : `setup-broker-tag-${secret.name}`
                  }
                  className={cm.textMuted}
                  style={{
                    flexShrink: 0,
                    fontSize: 10,
                    padding: '2px 8px',
                    borderRadius: 100,
                    background:
                      'color-mix(in srgb, var(--color-foreground-secondary) 12%, transparent)',
                  }}
                >
                  {t('workspace.setup.brokerTag', undefined, { defaultValue: 'Molecule' })}
                </span>
              </>
            )}
            {note}
          </span>
        ) : secret.status === 'set' ? (
          <span className={cm.textMuted} style={{ fontSize: 11 }}>
            {secret.masked ??
              t('workspace.setup.alreadySet', undefined, { defaultValue: '(already set)' })}
          </span>
        ) : (
          <input
            type={isSensitiveKey(secret.name) ? 'password' : 'text'}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={secret.example ?? ''}
            autoComplete="off"
            disabled={locked}
            style={{
              flex: 1,
              minWidth: 0,
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border-secondary)',
              borderRadius: 4,
              padding: '4px 8px',
              fontSize: inputFontSize,
              fontFamily: 'monospace',
              color: 'var(--mol-color-text, #e0e0e0)',
              outline: 'none',
            }}
          />
        )}
      </div>
      {/* Override input: while the user runs their own keys, molecule-supplied
          managed/provisioned rows accept a typed replacement — saved via the
          normal flow, which overwrites the managed vault row (BYO takeover). */}
      {override && (secret.vaultSource === 'managed' || secret.status === 'provisioned') && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 4,
            paddingLeft: indent,
          }}
        >
          <input
            type={isSensitiveKey(secret.name) ? 'password' : 'text'}
            data-mol-id={`setup-override-${secret.name}`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={locked}
            placeholder={t('workspace.setup.overridePlaceholder', undefined, {
              defaultValue: 'Paste your own key to override',
            })}
            autoComplete="off"
            style={{
              flex: 1,
              minWidth: 0,
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border-secondary)',
              borderRadius: 4,
              padding: '4px 8px',
              fontSize: inputFontSize,
              fontFamily: 'monospace',
              color: 'var(--mol-color-text, #e0e0e0)',
              outline: 'none',
            }}
          />
        </div>
      )}
      {/* Broker/managed-provided values need no get-a-key guidance — same
          suppression as provisioned/auto. */}
      {secret.instructions &&
        secret.status !== 'auto' &&
        secret.status !== 'provisioned' &&
        secret.vaultSource !== 'broker' &&
        secret.vaultSource !== 'managed' && (
          <div
            className={cm.textMuted}
            style={{ fontSize: 11, marginTop: 3, paddingLeft: indent, lineHeight: 1.5 }}
          >
            {secret.instructions}
            {secret.setupUrl && (
              <>
                {' '}
                <a
                  className={cm.textPrimary}
                  href={secret.setupUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('workspace.setup.getIt', undefined, { defaultValue: 'Get it here' })} →
                </a>
              </>
            )}
          </div>
        )}
    </div>
  )
}

/**
 * One setup integration's credential block: optional heading (label + mono
 * package name — shown when nested inside a merged service row), the rich
 * per-secret rows, and a right-aligned Save for missing keys. Shared with the
 * Storage panel (the same credential rows on its storage-type rows).
 * @param root0 - Component props.
 * @param root0.integration - The integration to render.
 * @param root0.values - Draft values keyed by secret name.
 * @param root0.onValueChange - Draft change handler.
 * @param root0.saving - Whether a save is in flight.
 * @param root0.onSave - Saves the entered keys.
 * @param root0.showHeading - Whether to render the integration heading.
 * @param root0.override - The "Use your own keys" switch is ON: managed and
 *   provisioned rows grow an own-key input (saving overwrites the managed
 *   vault row — the BYO takeover) and Save is offered for the typed drafts.
 * @param root0.locked - Credential writes need an admin role and a registered
 *   account; without them the inputs and Save render disabled rather than
 *   staying interactive all the way to a 403.
 * @returns The rendered block.
 */
export function IntegrationSecrets({
  integration,
  values,
  onValueChange,
  saving,
  onSave,
  showHeading,
  override,
  locked,
}: {
  integration: SetupIntegration
  values: Record<string, string>
  onValueChange: (name: string, value: string) => void
  saving: boolean
  onSave: (integration: SetupIntegration) => void
  showHeading: boolean
  override?: boolean
  locked?: boolean
}) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const shortId = integration.package.replace('@molecule/', '')
  return (
    <div data-mol-id={`env-nested-${shortId}`}>
      {showHeading && (
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 6,
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--color-foreground-secondary)',
            marginBottom: 4,
          }}
        >
          {integration.label ?? integration.package}
          <span
            className={cm.textMuted}
            style={{ fontSize: 10, fontFamily: 'monospace', fontWeight: 400 }}
          >
            {[integration.package, ...(integration.absorbedPackages ?? [])].join(', ')}
          </span>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {integration.secrets.map((secret) => (
          <SecretRow
            key={secret.name}
            secret={secret}
            value={values[secret.name] || ''}
            onChange={(value) => onValueChange(secret.name, value)}
            override={override}
            locked={locked}
          />
        ))}
      </div>
      {(integration.secrets.some((s) => s.status === 'missing') || override) && (
        <Flex justify="end" style={{ marginTop: 10 }}>
          <Button
            size="sm"
            variant="outline"
            data-mol-id={`setup-save-${integration.package.replace('@molecule/', '')}`}
            disabled={saving || locked || !integration.secrets.some((s) => values[s.name]?.trim())}
            onClick={() => onSave(integration)}
          >
            {t('workspace.setup.save', undefined, { defaultValue: 'Save' })}
          </Button>
        </Flex>
      )}
    </div>
  )
}

/**
 * Saves the entered secret drafts for one integration via
 * `PUT /projects/:id/env`, emitting the saved-live / saved-next-start /
 * error notification. Shared by the Environment and Storage panels so the
 * save behavior (and its copy) stays identical everywhere credential rows
 * render.
 * @param http - The HTTP client.
 * @param projectId - The project whose env vars to write.
 * @param integration - The integration whose entered drafts to save.
 * @param values - All secret drafts, keyed by secret name.
 * @param t - The translate function (notification copy).
 * @param scope - Which environment to write the values to (default: shared).
 * @param onAccessDenied - Called when the save was REFUSED rather than failed:
 * `requiresSignup` (a guest owner — the vault needs a registered account) or
 * `forbidden` (a non-admin member). Lets the panel render a persistent notice
 * with a way forward instead of leaving a one-off toast as the only feedback.
 * @returns The names of the keys that were saved (so the caller can clear
 * those drafts and refresh), or null when nothing was entered or the save
 * failed (the error notification has already been emitted).
 */
export async function saveIntegrationSecrets(
  http: HttpClient,
  projectId: string,
  integration: SetupIntegration,
  values: Record<string, string>,
  t: TranslateFunction,
  scope: EnvScope = 'all',
  onAccessDenied?: (kind: 'requiresSignup' | 'forbidden') => void,
): Promise<string[] | null> {
  const envVars: Record<string, string> = {}
  for (const secret of integration.secrets) {
    const val = values[secret.name]?.trim()
    if (val) envVars[secret.name] = val
  }
  if (Object.keys(envVars).length === 0) return null

  try {
    const res = await http.put<EnvSaveResponse>(`/projects/${projectId}/env`, {
      envVars,
      environment: scope,
    })
    notifyEnvSaveOutcome(t, scope, res.data)
    return Object.keys(envVars)
  } catch (error) {
    // The vault's two DELIBERATE denials each have a next step, and neither is
    // "try again": a guest needs an account to own credentials, and a
    // non-admin member needs a role change. Reporting both as one generic
    // failure left an anonymous user clicking Save forever.
    if (requiresSignup(error)) {
      onAccessDenied?.('requiresSignup')
      emitNotification(
        t('workspace.setup.saveRequiresSignup', undefined, {
          defaultValue: 'Sign up free to save credentials — your keys are kept in your account.',
        }),
        'error',
        {
          label: t('environment.guestSignup', undefined, { defaultValue: 'Sign up free' }),
          href: '/signup',
        },
      )
      return null
    }
    if (isInsufficientRoleError(error)) {
      onAccessDenied?.('forbidden')
      emitNotification(
        t('workspace.setup.saveForbidden', undefined, {
          defaultValue: 'Saving credentials needs an admin role on this project.',
        }),
        'error',
      )
      return null
    }
    console.error('saveIntegrationSecrets: failed to save env vars', error)
    emitNotification(
      t('workspace.setup.saveError', undefined, {
        defaultValue: 'Could not save the keys — check your access and try again.',
      }),
      'error',
    )
    return null
  }
}

// ── Service row body (merged and catalog-only) ───────────────────────────────

/**
 * The expanded body of a service-category row, laid out on the ONE template
 * every expanded row body uses (Environment service rows, Environment package
 * rows, Storage rows):
 *
 *  1. the catalog description (muted, 12px);
 *  2. ONE explanatory muted line ({@link MutedNote} — managed / coming-soon /
 *     why-BYO), never a boxed notice, never stacked;
 *  3. the right-aligned "Use your own keys" switch — offered ONLY where
 *     molecule's hosted backend (the molecule default) and a user alternative
 *     both exist (class A + live brokerSupported, in-app);
 *  4. content: switch OFF → the managed content (the billing line, no
 *     per-secret rows at all); switch ON (or no switch) → the nested setup
 *     integrations' rich credential rows + the services-side BYO inputs for
 *     env keys NOT already covered by a nested setup secret (the dedup rule —
 *     the richest rendering wins, never the same key twice).
 *
 * The switch drives the REAL wiring: turning it ON calls disableBroker
 * (records the opt-out) and reveals the credential inputs; turning it OFF
 * calls enableBroker. Missing required keys derive it ON (the setup flow
 * always leads with its inputs). A fully-managed secret set on a row without
 * a switch collapses to the managed line — no per-secret rows.
 * @param root0 - Component props.
 * @param root0.entry - The catalog entry.
 * @param root0.live - The category's live backend state, when loaded.
 * @param root0.state - The derived service state.
 * @param root0.billing - The plan's broker rate card, when loaded.
 * @param root0.busy - Whether an action for this category is in flight.
 * @param root0.vaultLocked - Vault access unavailable (guest / non-admin) —
 *   the switch's enable/disable actions would 403, so it renders disabled.
 * @param root0.inApp - Whether this is an "In your app" row (discovery rows never grow the switch).
 * @param root0.nested - Setup integrations merged under this category.
 * @param root0.values - Setup-secret draft values.
 * @param root0.onValueChange - Setup-secret draft change handler.
 * @param root0.savingSetup - Whether a setup-key save is in flight.
 * @param root0.onSaveSetup - Saves a nested integration's entered keys.
 * @param root0.drafts - BYO key draft values.
 * @param root0.onDraftChange - BYO draft change handler.
 * @param root0.onBroker - Enable/disable brokered mode for the category.
 * @param root0.onPutSecret - Store a BYO secret for an env key.
 * @param root0.onDeleteSecret - Remove a stored secret by env key.
 * @returns The rendered body.
 */
function ServiceRowBody({
  entry,
  live,
  state,
  billing,
  busy,
  vaultLocked,
  inApp,
  nested,
  values,
  onValueChange,
  savingSetup,
  onSaveSetup,
  drafts,
  onDraftChange,
  onBroker,
  onPutSecret,
  onDeleteSecret,
}: {
  entry: ServiceCatalogEntry
  live: LiveServiceState | undefined
  state: ServiceState
  billing: ServicesBilling | null
  busy: boolean
  vaultLocked: boolean
  inApp: boolean
  nested: SetupIntegration[]
  values: Record<string, string>
  onValueChange: (name: string, value: string) => void
  savingSetup: boolean
  onSaveSetup: (integration: SetupIntegration) => void
  drafts: Record<string, string>
  onDraftChange: (key: string, value: string) => void
  onBroker: (entry: ServiceCatalogEntry, enable: boolean) => void
  onPutSecret: (entry: ServiceCatalogEntry, key: string, value: string) => Promise<boolean>
  onDeleteSecret: (entry: ServiceCatalogEntry, key: string) => void
}) {
  const cm = getClassMap()
  const { t } = useTranslation()
  // Same phone treatment as SecretRow: full-width key label above the input
  // and 16px input font (iOS zooms any focusable field below 16px).
  const isMobile = useIsMobile()
  const coarse = useMediaQuery('(pointer: coarse)')
  const inputFontSize = isMobile || coarse ? 16 : 12
  const isClassA = entry.serviceClass === 'A'
  // The switch is offered ONLY where molecule can broker TODAY — the backend
  // 400s a POST /vault/broker for unsupported categories, so gate on the live
  // flag (fails closed while the live state is unavailable) — and only on
  // in-app rows (discovery rows have nothing wired to switch away from).
  const brokerSupported = live?.brokerSupported === true
  const showSwitch = isClassA && brokerSupported && inApp
  const frame = billingFrame(billing)

  const nestedSecrets = nested.flatMap((i) => i.secrets)
  const hasMissingRequired = nestedSecrets.some((s) => s.required && s.status === 'missing')
  // "Your keys": a BYO vault key, or a nested secret the user set themselves
  // (molecule's broker AND managed rows are molecule's, never the user's).
  const userKeySet = state.byoConfigured || nestedSecrets.some(isUserSetSecret)
  // The switch reflects the REAL wiring: ON when the user opted out of the
  // hosted default or runs their own keys; missing required keys derive it ON
  // so the setup flow always leads with its inputs.
  const ownKeys = state.optedOut || userKeySet || (!state.brokered && hasMissingRequired)

  // Credential rows show while the user runs their own keys. Without a switch
  // (BYO-only categories) they ARE the row's content — except a fully-managed
  // secret set, which collapses to the managed line (nothing to configure).
  const showNested = nested.length > 0 && (showSwitch ? ownKeys : !allSecretsManaged(nestedSecrets))
  const showByo = showSwitch ? ownKeys : !state.brokered

  // Dedup rule: env keys already rendered by a nested setup secret never get a
  // second services-side input — the rich setup rendering wins.
  const coveredKeys = new Set(nested.flatMap((i) => i.secrets.map((s) => s.name)))
  const byoProviders = entry.providers
    .map((provider) => ({
      ...provider,
      envKeys: provider.envKeys.filter((key) => !coveredKeys.has(key)),
    }))
    .filter((provider) => provider.envKeys.length > 0)

  return (
    <>
      {/* 1 — description */}
      <p
        style={{
          margin: '0 0 10px',
          fontSize: 12,
          color: 'var(--color-foreground-secondary)',
        }}
      >
        {t(`services.category.${entry.category}.desc`, undefined, {
          defaultValue: entry.description,
        })}
      </p>

      {/* 2+3 — one SETTINGS ROW: the explanatory line (left) and the switch
          (right) share a line, so the control never floats alone between two
          left-aligned text lines. */}
      {showSwitch ? (
        <Flex align="center" justify="between" gap="sm" style={{ marginBottom: 10 }}>
          <MutedNote molId={`services-managed-${entry.category}`} style={{ margin: 0, flex: 1 }}>
            {ownKeys
              ? t('services.ownKeysHint', undefined, {
                  defaultValue:
                    "Using your own keys — Molecule's hosted backend is off for this service.",
                })
              : t('services.brokeredHint', undefined, {
                  defaultValue: "Runs on Molecule's hosted backend — instant, no setup required.",
                })}
          </MutedNote>
          <Switch
            data-mol-id={`services-own-switch-${entry.category}`}
            label={t('services.useYourOwnKeys', undefined, { defaultValue: 'Use your own keys' })}
            checked={ownKeys}
            size="sm"
            // Locked for guests / non-admins: the enable/disable actions need
            // vault access (they 403 otherwise), so the control must not look
            // interactive — the callout at the top explains why.
            disabled={busy || vaultLocked}
            // ON→OFF re-enables the hosted default; OFF→ON records the
            // opt-out (disableBroker) and reveals the credential inputs.
            onChange={() => onBroker(entry, ownKeys)}
          />
        </Flex>
      ) : allSecretsManaged(nestedSecrets) && nested.length > 0 ? (
        <MutedNote molId={`services-managed-${entry.category}`}>
          {t('environment.managedNote', undefined, {
            defaultValue: 'Managed by Molecule — configured automatically, nothing to do.',
          })}
        </MutedNote>
      ) : isClassA && !brokerSupported ? (
        <MutedNote molId={`services-hosted-coming-soon-${entry.category}`}>
          {t('services.hostedComingSoon', undefined, {
            defaultValue: 'Hosted by Molecule coming soon — bring your own key for now.',
          })}
        </MutedNote>
      ) : !isClassA && entry.byoReason ? (
        <MutedNote molId={`services-byo-reason-${entry.category}`}>
          {t(`services.byoReason.${entry.category}`, undefined, { defaultValue: entry.byoReason })}
        </MutedNote>
      ) : null}

      {/* 4 — managed content: the billing line (how the hosted backend's
          cost works on this plan) while the switch is OFF */}
      {showSwitch && !ownKeys && frame && (
        <MutedNote molId={`services-billing-note-${entry.category}`}>
          {frame.kind === 'freeTrial' ? (
            <>
              {t(
                'services.billing.freeTrial',
                { count: frame.requests },
                {
                  defaultValue: `Free trial: ${frame.requests} hosted requests per month included — see`,
                },
              )}{' '}
              <Link
                to="/pricing"
                data-mol-id={`services-pricing-link-${entry.category}`}
                className={cm.textPrimary}
              >
                {t('services.billing.pricingLink', undefined, { defaultValue: 'Pricing' })}
              </Link>{' '}
              {t('services.billing.freeTrialPaidPlans', undefined, {
                defaultValue: 'for included usage on paid plans.',
              })}
            </>
          ) : (
            // The included amount is a concrete entitlement they already pay
            // for, so it stays. The `{markup}× provider cost` clause does not:
            // it publishes our cost basis and an exact rate we may move.
            t(
              'services.billing.included',
              { amount: formatCentsCompact(frame.includedCents) },
              {
                defaultValue: `${formatCentsCompact(frame.includedCents)} of hosted usage included monthly — then from your prepaid balance.`,
              },
            )
          )}
        </MutedNote>
      )}

      {/* 4 — credential content: the nested setup integrations' rich
          per-secret rows (hidden entirely while molecule manages the row) */}
      {showNested &&
        nested.map((integration) => (
          <div key={integration.package} style={{ marginBottom: 10 }}>
            <IntegrationSecrets
              integration={integration}
              values={values}
              onValueChange={onValueChange}
              saving={savingSetup}
              onSave={onSaveSetup}
              showHeading
              locked={vaultLocked}
            />
          </div>
        ))}

      {/* Services-side BYO key entry — only env keys no nested secret covers */}
      {showByo && byoProviders.length > 0 && (
        <div data-mol-id={`services-byo-${entry.category}`}>
          {byoProviders.map((provider) => (
            <div key={provider.id} style={{ marginBottom: 8 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--color-foreground-secondary)',
                  marginBottom: 4,
                }}
              >
                {provider.label}
              </div>
              {provider.envKeys.map((key) => {
                const masked = state.byoMasked[key]
                return (
                  // A plain flex div, NOT <Flex>: the Flex component doesn't
                  // spread rest props, so a data-mol-id on it never reaches the
                  // DOM (which silently ate `services-key-*` in the old panel).
                  <div
                    key={key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      marginBottom: 4,
                      // Never engages on desktop (200px label + flexible value
                      // fit); on phones the full-width label wraps the value below.
                      flexWrap: 'wrap',
                    }}
                    data-mol-id={`services-key-${key}`}
                  >
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 11,
                        color: 'var(--mol-color-text-secondary, #888)',
                        width: isMobile ? '100%' : 200,
                        flexShrink: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {key}
                    </span>
                    {masked ? (
                      <>
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            fontFamily: 'monospace',
                            fontSize: 11,
                            color: 'var(--mol-color-text, #e0e0e0)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {masked}
                        </span>
                        <Tooltip
                          content={t('services.remove', undefined, { defaultValue: 'Remove' })}
                          placement="left"
                        >
                          {/* mol-bespoke-button: icon-only × for one stored
                              secret, no label — the EnvVarEditor row-delete
                              twin; dense 14px glyph, tokens throughout, 40px
                              touch floor on phones. */}
                          <button
                            type="button"
                            data-mol-id={`services-remove-${key}`}
                            disabled={busy || vaultLocked}
                            onClick={() => onDeleteSecret(entry, key)}
                            aria-label={t('services.remove', undefined, { defaultValue: 'Remove' })}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'var(--color-surface)',
                              border: '1px solid var(--color-border-secondary)',
                              borderRadius: 4,
                              color: 'var(--mol-color-error, #c83c3c)',
                              cursor: busy ? 'not-allowed' : 'pointer',
                              padding: '4px 6px',
                              flexShrink: 0,
                              // Touch floor for the dense-row icon button.
                              ...(isMobile ? { minWidth: 40, minHeight: 40 } : {}),
                            }}
                          >
                            <Icon name="x-mark" size={14} />
                          </button>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <input
                          type={isSensitiveKey(key) ? 'password' : 'text'}
                          data-mol-id={`services-input-${key}`}
                          value={drafts[key] ?? ''}
                          onChange={(e) => onDraftChange(key, e.target.value)}
                          placeholder={t('services.keyPlaceholder', undefined, {
                            defaultValue: 'Paste key',
                          })}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            height: isMobile ? 40 : 32,
                            boxSizing: 'border-box',
                            background: 'var(--color-surface)',
                            border: '1px solid var(--color-border-secondary)',
                            borderRadius: 4,
                            padding: '4px 8px',
                            fontSize: inputFontSize,
                            fontFamily: 'monospace',
                            color: 'var(--mol-color-text, #e0e0e0)',
                            outline: 'none',
                          }}
                          disabled={vaultLocked}
                          onKeyDown={(e) => {
                            if (
                              e.key === 'Enter' &&
                              (drafts[key] ?? '').trim() &&
                              !busy &&
                              !vaultLocked
                            ) {
                              // Clear ONLY after the store succeeds: this used to
                              // fire unconditionally, so a refused save (guest,
                              // non-admin, a rejected key) also wiped the key the
                              // user had just pasted.
                              void onPutSecret(entry, key, drafts[key]).then((stored) => {
                                if (stored) onDraftChange(key, '')
                              })
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          data-mol-id={`services-save-${key}`}
                          disabled={busy || vaultLocked || !(drafts[key] ?? '').trim()}
                          onClick={() => {
                            void onPutSecret(entry, key, drafts[key]).then((stored) => {
                              if (stored) onDraftChange(key, '')
                            })
                          }}
                          // Same height as the key input beside it at both
                          // breakpoints (a fixed 32 was 8px short of the 40px
                          // input on phones/touch).
                          style={{ height: isMobile ? 40 : 32 }}
                        >
                          {t('services.save', undefined, { defaultValue: 'Save' })}
                        </Button>
                      </>
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </>
  )
}

// ── Network access (the sandbox egress allowlist) ────────────────────────────

/** One recorded blocked outbound connection from `GET /projects/:id/egress`. */
export interface EgressDenial {
  host: string
  port: number
  count: number
  firstAt: number
  lastAt: number
}

/**
 * The `GET /projects/:id/egress` envelope — the allow LAYERS plus the union
 * actually applied to the sandbox. Only `allowlist` is the user's to edit; the
 * other three render read-only so "why can my app reach that?" has a visible
 * answer instead of collapsing into one undifferentiated list.
 */
export interface EgressState {
  /** Platform defaults — package registries, git, toolchain downloads. */
  defaults: string[]
  /** Molecule's own endpoints (activity ingest, key broker). */
  platform: string[]
  /** Hosts implied by the project's installed `@molecule/*` packages. */
  packageHosts: string[]
  /**
   * The project's OWN deployed origins. Always allowed: a project that cannot
   * fetch back the site it just deployed cannot check its own work, and
   * neither can Synthase.
   */
  deploy: string[]
  /** Hosts the USER added for this project — the editable layer. */
  allowlist: string[]
  /**
   * Hosts the project owner approved once on their account — allowed in every
   * project they own, so none of them asks again.
   */
  account: string[]
  /** Full access: every public host is reachable (private addresses never are). */
  open: boolean
  /** The union actually enforced on the sandbox. */
  effective: string[]
  /**
   * Whether a blocked connection produces a visible denial. FALSE on a
   * provider-enforced sandbox: it drops the packet at its own network layer and
   * reports nothing back, so the request just hangs and no denial — hence no
   * `egress_denial` chat card — ever arrives. The section must not imply one
   * will, which is exactly why the allowlist is editable up front.
   */
  denialsObserved: boolean
  denials: EgressDenial[]
}

/** Keeps only the non-empty strings of a possibly-malformed array field. */
const stringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    : []

/**
 * Coerces the egress response into a fully-populated {@link EgressState}. One
 * fetch drives five lists, so a single missing or malformed field must degrade
 * to an empty layer rather than blank the whole section.
 * @param data - The raw response body.
 * @returns The normalized state.
 */
export function normalizeEgressState(data: unknown): EgressState {
  const raw = (data ?? {}) as Record<string, unknown>
  const rawDenials = (Array.isArray(raw.denials) ? raw.denials : []).filter(
    (denial): denial is Record<string, unknown> => typeof denial === 'object' && denial !== null,
  )
  return {
    defaults: stringList(raw.defaults),
    platform: stringList(raw.platform),
    packageHosts: stringList(raw.packageHosts),
    deploy: stringList(raw.deploy),
    allowlist: stringList(raw.allowlist),
    account: stringList(raw.account),
    open: raw.open === true,
    effective: stringList(raw.effective),
    denialsObserved: raw.denialsObserved === true,
    denials: rawDenials
      .filter((denial) => typeof denial.host === 'string' && denial.host.trim().length > 0)
      .map((denial) => ({
        host: denial.host as string,
        port: typeof denial.port === 'number' ? denial.port : 0,
        count: typeof denial.count === 'number' ? denial.count : 1,
        firstAt: typeof denial.firstAt === 'number' ? denial.firstAt : 0,
        lastAt: typeof denial.lastAt === 'number' ? denial.lastAt : 0,
      })),
  }
}

/**
 * One plain sentence for a failed allowlist write. The API's two expected
 * refusals (an unusable host, a full list) each say what to do about it;
 * anything else gets the generic retry line.
 * @param error - The caught HTTP error.
 * @param t - The translate function.
 * @returns The message for the section's error notice.
 */
export function egressErrorMessage(error: unknown, t: TranslateFunction): string {
  const key = errorKeyOf(error)
  if (key === 'projectOps.error.egressInvalidHost') {
    return t('environment.network.invalidHost', undefined, {
      defaultValue:
        "That isn't a valid host. Use a hostname like api.example.com or *.example.com.",
    })
  }
  if (key === 'projectOps.error.egressAllowlistFull') {
    return t('environment.network.listFull', undefined, {
      defaultValue: 'This list is full. Remove a host before adding another.',
    })
  }
  return t('environment.network.updateError', undefined, {
    defaultValue: 'Could not update network access. Try again.',
  })
}

/**
 * The **Network access** section — the project's sandbox egress allowlist.
 *
 * Sandboxes are deny-by-default on the network, and where the provider enforces
 * that itself a blocked connection produces no signal at all (see
 * {@link EgressState.denialsObserved}) — the request simply hangs and the
 * reactive `egress_denial` chat card never fires. So this section, not a
 * denial queue, is where a user both SEES what their app can reach and fixes
 * "it can't reach my vendor's API".
 *
 * Renders as the panel's own collapsible rows: three read-only layers, each
 * labelled with why its hosts are allowed, plus the user's own list with an add
 * field and a per-host Remove. Where denials ARE observed, each recently
 * blocked host gets a one-click Allow.
 * @param root0 - Component props.
 * @param root0.projectId - Project whose allowlist to show.
 * @param root0.expandedKey - The panel's single-open accordion key.
 * @param root0.onToggle - Toggles a row in the panel's accordion.
 * @param root0.first - Whether this is the panel's first section.
 * @returns The rendered section.
 */
export function NetworkAccessSection({
  projectId,
  expandedKey,
  onToggle,
  first,
  canEdit,
}: {
  projectId: string
  expandedKey: string | null
  onToggle: (key: string) => void
  first?: boolean
  /** False for read-only viewers: allow/add/remove controls are hidden. */
  canEdit?: boolean
}) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  // 16px input font on phones/touch — iOS zooms any focusable field below 16px.
  const isMobile = useIsMobile()
  const coarse = useMediaQuery('(pointer: coarse)')
  const inputFontSize = isMobile || coarse ? 16 : 12

  const [state, setState] = useState<EgressState | null>(null)
  const [draft, setDraft] = useState('')
  /** Host whose row is mutating — disables that row's Remove / Allow. */
  const [busyHost, setBusyHost] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  /** A failed WRITE (add/remove), already turned into one plain sentence. */
  const [actionError, setActionError] = useState<string | null>(null)
  /** A failed READ — the poll keeps retrying, so this clears itself. */
  const [loadFailed, setLoadFailed] = useState(false)
  /**
   * A write owns the allowlist while it is in flight: the poll below would
   * otherwise land mid-write and snap the list back to the pre-write server
   * state, undoing the row the user just clicked.
   */
  const mutating = useRef(false)

  const load = useCallback(async () => {
    try {
      const res = await http.get<EgressState>(`/projects/${projectId}/egress`)
      if (!mutating.current) setState(normalizeEgressState(res.data))
      setLoadFailed(false)
    } catch (error) {
      console.error('EnvironmentPanel: failed to load network access', error)
      setLoadFailed(true)
    }
  }, [http, projectId])

  useEffect(() => {
    void load()
    // The same visibility-aware 10s cadence as the rest of the panel: the
    // allowlist also changes from OUTSIDE this view (the egress_denial chat
    // card's Allow), and observed denials accrue while the user is looking.
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') void load()
    }, 10_000)
    return () => clearInterval(interval)
  }, [load])

  // Live cross-client refresh — another member's Allow/revoke lands at once.
  useEffect(() => onResourceInvalidated('env', () => void load()), [load])

  /**
   * Adds or removes one host. Optimistic — the row appears/disappears on the
   * click — but correct: the server's returned list replaces the guess, a
   * failure restores exactly what was there, and a refresh follows so the
   * derived state (effective hosts, cleared denials) matches.
   */
  const mutateAllowlist = useCallback(
    async (host: string, action: 'add' | 'remove'): Promise<boolean> => {
      const target = host.trim()
      if (!target) return false
      const snapshot = state
      mutating.current = true
      setActionError(null)
      setState((prev) =>
        prev
          ? {
              ...prev,
              allowlist:
                action === 'add'
                  ? prev.allowlist.includes(target)
                    ? prev.allowlist
                    : [...prev.allowlist, target]
                  : prev.allowlist.filter((entry) => entry !== target),
            }
          : prev,
      )
      try {
        const res =
          action === 'add'
            ? await http.post<{ allowlist: string[] }>(`/projects/${projectId}/egress/allowlist`, {
                host: target,
              })
            : await http.delete<{ allowlist: string[] }>(
                `/projects/${projectId}/egress/allowlist`,
                { data: { host: target } },
              )
        // The response carries the canonical list (the API lowercases hosts and
        // normalizes wildcards), so it wins over the optimistic guess.
        setState((prev) => (prev ? { ...prev, allowlist: stringList(res.data?.allowlist) } : prev))
        mutating.current = false
        await load()
        return true
      } catch (error) {
        console.error('EnvironmentPanel: egress allowlist update failed', error)
        setState(snapshot)
        setActionError(egressErrorMessage(error, t))
        return false
      } finally {
        mutating.current = false
      }
    },
    [http, projectId, state, load, t],
  )

  const handleAdd = useCallback(async () => {
    const host = draft.trim()
    if (!host || adding) return
    setAdding(true)
    const ok = await mutateAllowlist(host, 'add')
    // The field keeps a rejected host so the user can correct it in place.
    if (ok) setDraft('')
    setAdding(false)
  }, [draft, adding, mutateAllowlist])

  const handleHostAction = useCallback(
    async (host: string, action: 'add' | 'remove') => {
      setBusyHost(host)
      await mutateAllowlist(host, action)
      setBusyHost(null)
    },
    [mutateAllowlist],
  )

  const allowlist = state?.allowlist ?? []
  // Denials exist only where the provider reports them. An approved host drops
  // off immediately (the server clears its denials too), so the row disappears
  // on the click rather than lingering until the next poll.
  const denials = (state?.denialsObserved ? state.denials : [])
    .filter((denial) => !allowlist.includes(denial.host))
    .sort((a, b) => b.lastAt - a.lastAt)

  /** The count chip shared by every layer row. */
  const countChip = (molId: string, count: number, tone: ChipTone) => (
    <Chip tone={tone} molId={molId}>
      {t(
        'environment.network.hostCount',
        { count },
        { defaultValue: `${count} host${count === 1 ? '' : 's'}` },
      )}
    </Chip>
  )

  /** One read-only layer row: why these hosts are allowed, then the hosts. */
  const readOnlyRow = (
    id: string,
    icon: IconName,
    label: string,
    note: string,
    hosts: string[],
  ) => {
    const rowKey = `net:${id}`
    return (
      <EnvRow
        molId={`network-access-row-${id}`}
        icon={icon}
        label={label}
        chip={countChip(`network-access-count-${id}`, hosts.length, 'managed')}
        expanded={expandedKey === rowKey}
        onToggle={() => onToggle(rowKey)}
      >
        <MutedNote molId={`network-access-note-${id}`}>{note}</MutedNote>
        <div
          data-mol-id={`network-access-list-${id}`}
          className={cm.textMuted}
          style={{ fontSize: 11, fontFamily: 'monospace', lineHeight: 1.7, wordBreak: 'break-all' }}
        >
          {hosts.map((host) => (
            <div key={host}>{host}</div>
          ))}
        </div>
      </EnvRow>
    )
  }

  return (
    <div data-mol-id="network-access">
      <SectionHeading molId="network-access-heading" first={first}>
        {t('environment.network.heading', undefined, { defaultValue: 'Network access' })}
      </SectionHeading>
      <MutedNote molId="network-access-intro">
        {t('environment.network.intro', undefined, {
          defaultValue: 'Your app can only reach the hosts listed here.',
        })}
      </MutedNote>
      {/* Where the provider drops blocked packets itself, nothing ever reports
          the block — say so, instead of leaving the user waiting for a denial
          that cannot arrive. */}
      {state?.open && (
        <PanelNotice tone="info" molId="network-access-full">
          {t('environment.network.fullAccess', undefined, {
            defaultValue:
              'Full access is on: your app can reach any public host. Private and internal addresses stay blocked. Change it in Settings.',
          })}
        </PanelNotice>
      )}
      {state !== null && !state.open && !state.denialsObserved && (
        <MutedNote molId="network-access-silent-note">
          {t('environment.network.silentNote', undefined, {
            defaultValue:
              "Blocked connections aren't reported — the request just hangs. Add a host if your app can't reach it.",
          })}
        </MutedNote>
      )}
      {loadFailed && (
        <PanelNotice tone="error" molId="network-access-load-error">
          {t('environment.network.loadError', undefined, {
            defaultValue: "Couldn't load network access — retrying shortly.",
          })}
        </PanelNotice>
      )}
      {actionError !== null && (
        <PanelNotice tone="error" molId="network-access-error">
          {actionError}
        </PanelNotice>
      )}

      {/* Recently blocked, where the provider reports them at all — each one a
          host the user can approve in a click, the same choice the chat card
          offers when it fires. */}
      {denials.length > 0 && (
        <>
          <MutedNote molId="network-access-denials-note">
            {t('environment.network.denialsNote', undefined, {
              defaultValue: 'Blocked recently. Allow a host only if you expected the connection.',
            })}
          </MutedNote>
          {denials.map((denial) => (
            <Flex
              key={`${denial.host}:${denial.port}`}
              data-mol-id={`network-access-denial-${denial.host}`}
              align="center"
              gap="sm"
              style={{ marginBottom: 6 }}
            >
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: 'monospace',
                  fontSize: 12,
                  wordBreak: 'break-all',
                }}
              >
                {denial.port ? `${denial.host}:${denial.port}` : denial.host}
              </span>
              <Chip tone="warning" molId={`network-access-denial-count-${denial.host}`}>
                {t(
                  'environment.network.blockedCount',
                  { count: denial.count },
                  { defaultValue: `${denial.count} blocked` },
                )}
              </Chip>
              {canEdit !== false && (
                <Button
                  size="sm"
                  variant="outline"
                  data-mol-id={`network-access-allow-${denial.host}`}
                  disabled={busyHost === denial.host}
                  onClick={() => void handleHostAction(denial.host, 'add')}
                  style={{ height: 32 }}
                >
                  {t('environment.network.allow', undefined, { defaultValue: 'Allow' })}
                </Button>
              )}
            </Flex>
          ))}
        </>
      )}

      {/* The one editable layer. */}
      <EnvRow
        molId="network-access-row-allowlist"
        icon="globe"
        label={t('environment.network.yourHosts', undefined, { defaultValue: 'Hosts you allowed' })}
        chip={countChip(
          'network-access-count-allowlist',
          allowlist.length,
          allowlist.length > 0 ? 'byo' : 'none',
        )}
        expanded={expandedKey === 'net:allowlist'}
        onToggle={() => onToggle('net:allowlist')}
      >
        <MutedNote molId="network-access-note-allowlist">
          {t('environment.network.yourHostsNote', undefined, {
            defaultValue: 'Add any host your app needs to reach.',
          })}
        </MutedNote>
        <Flex align="center" gap="xs" style={{ marginBottom: 10 }}>
          <input
            type="text"
            data-mol-id="network-access-input"
            value={draft}
            disabled={canEdit === false}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('environment.network.hostPlaceholder', undefined, {
              defaultValue: 'api.example.com',
            })}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            style={{
              flex: 1,
              minWidth: 0,
              height: isMobile ? 40 : 32,
              boxSizing: 'border-box',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border-secondary)',
              borderRadius: 4,
              padding: '4px 8px',
              fontSize: inputFontSize,
              fontFamily: 'monospace',
              color: 'var(--mol-color-text, #e0e0e0)',
              outline: 'none',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleAdd()
            }}
          />
          {canEdit !== false && (
            <Button
              size="sm"
              variant="outline"
              data-mol-id="network-access-add"
              disabled={adding || !draft.trim()}
              onClick={() => void handleAdd()}
              // Same height as the host input beside it at both breakpoints.
              style={{ height: isMobile ? 40 : 32 }}
            >
              {t('environment.network.add', undefined, { defaultValue: 'Add' })}
            </Button>
          )}
        </Flex>
        {allowlist.length === 0 ? (
          <MutedNote molId="network-access-empty" style={{ marginBottom: 0 }}>
            {t('environment.network.empty', undefined, { defaultValue: 'No hosts added yet.' })}
          </MutedNote>
        ) : (
          allowlist.map((host) => (
            <Flex
              key={host}
              data-mol-id={`network-access-host-${host}`}
              align="center"
              gap="sm"
              style={{ padding: '3px 0' }}
            >
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: 'monospace',
                  fontSize: 12,
                  wordBreak: 'break-all',
                }}
              >
                {host}
              </span>
              {canEdit !== false && (
                <Button
                  size="sm"
                  variant="ghost"
                  data-mol-id={`network-access-remove-${host}`}
                  disabled={busyHost === host}
                  onClick={() => void handleHostAction(host, 'remove')}
                  style={{ height: 32 }}
                >
                  {t('environment.network.remove', undefined, { defaultValue: 'Remove' })}
                </Button>
              )}
            </Flex>
          ))
        )}
      </EnvRow>

      {/* The read-only layers, each saying why its hosts are already allowed.
          An empty layer renders no row — a project with no package-derived
          hosts should not grow an empty one. */}
      {state !== null &&
        state.defaults.length > 0 &&
        readOnlyRow(
          'defaults',
          'download',
          t('environment.network.defaults', undefined, {
            defaultValue: 'Package registries and tools',
          }),
          t('environment.network.defaultsNote', undefined, {
            defaultValue: 'Always allowed so npm, git, and the toolchain work.',
          }),
          state.defaults,
        )}
      {state !== null &&
        state.platform.length > 0 &&
        readOnlyRow(
          'platform',
          'logo-mark',
          t('environment.network.platform', undefined, { defaultValue: 'Molecule services' }),
          t('environment.network.platformNote', undefined, {
            defaultValue: "Molecule's own endpoints. Always allowed.",
          }),
          state.platform,
        )}
      {state !== null &&
        state.packageHosts.length > 0 &&
        readOnlyRow(
          'packages',
          'folder',
          t('environment.network.packages', undefined, {
            defaultValue: 'From installed packages',
          }),
          t('environment.network.packagesNote', undefined, {
            defaultValue: 'Allowed because a package you installed calls them.',
          }),
          state.packageHosts,
        )}
      {state !== null &&
        state.account.length > 0 &&
        readOnlyRow(
          'account',
          'user',
          t('environment.network.account', undefined, {
            defaultValue: 'Approved on your account',
          }),
          t('environment.network.accountNote', undefined, {
            defaultValue: 'Hosts you allowed once. Every project you own allows them.',
          }),
          state.account,
        )}
      {state !== null &&
        state.deploy.length > 0 &&
        readOnlyRow(
          'deploy',
          'rocket',
          t('environment.network.deploy', undefined, {
            defaultValue: 'Your deployed app',
          }),
          t('environment.network.deployNote', undefined, {
            defaultValue: 'Always allowed so your project can check its own live site.',
          }),
          state.deploy,
        )}
    </div>
  )
}

// ── The panel ────────────────────────────────────────────────────────────────

/** Props for {@link EnvironmentPanel}. */
interface EnvironmentPanelProps {
  /** Project whose environment (setup, services, env vars) to manage. */
  projectId: string
  /** False for read-only viewers: the egress-allowlist controls are hidden. */
  canEdit?: boolean
}

/**
 * The unified Environment panel — see the module doc above. Owns ONE
 * visibility-aware 10s poll cycle fetching `/setup`, `/services`, `/vault`
 * (and the env-var count) together; every vault/setup mutation refreshes all
 * of them. Draft input values live in separate state so a poll never clobbers
 * in-progress typing.
 * @param root0 - Component props.
 * @param root0.projectId - Project whose environment to manage.
 * @returns The rendered panel.
 */
export function EnvironmentPanel({ projectId, canEdit }: EnvironmentPanelProps) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  // 16px input font on phones/touch — iOS zooms any focusable field below 16px.
  const isMobile = useIsMobile()
  const coarse = useMediaQuery('(pointer: coarse)')
  const inputFontSize = isMobile || coarse ? 16 : 12

  const [integrations, setIntegrations] = useState<SetupIntegration[]>([])
  const [services, setServices] = useState<ServicesStateResponse | null>(null)
  const [vault, setVault] = useState<VaultResponse | null>(null)
  const [envCount, setEnvCount] = useState(0)
  const [loaded, setLoaded] = useState(false)
  /**
   * Vault reachability. The vault route is gated harder than the rest of the
   * modal (admin role + registered account), so its 403s are EXPECTED states
   * for guests ('requiresSignup') and non-admin members ('forbidden') — they
   * render as friendly notes, never the error banner. Only a real failure
   * (network / 5xx) is 'error'.
   */
  const [vaultAccess, setVaultAccess] = useState<'ok' | 'requiresSignup' | 'forbidden' | 'error'>(
    'ok',
  )
  /** A failed vault MUTATION (enable/disable broker, save/remove key). */
  const [actionError, setActionError] = useState<string | null>(null)
  /**
   * Whether credential WRITES are possible at all for this viewer. The vault
   * control plane is admin-only and registered-only, so a guest owner or a
   * non-admin member can read every status here and change none of it. Every
   * write control below reads this: they used to stay fully interactive and
   * only reveal the truth as a generic failure after the click — and the env
   * editor's add/delete revealed nothing at all.
   */
  const vaultLocked = vaultAccess !== 'ok'
  // Single-open accordion across the WHOLE view (services, packages, env vars).
  const [expanded, setExpanded] = useState<string | null>(null)
  // Setup-secret drafts, keyed by secret name — separate from fetched status so
  // the poll never clobbers in-progress input.
  const [values, setValues] = useState<Record<string, string>>({})
  // Services BYO drafts, keyed by env key — same poll-survival rationale.
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [savingSetup, setSavingSetup] = useState(false)
  // Category currently mutating — disables that row's controls.
  const [busyCategory, setBusyCategory] = useState<string | null>(null)
  // The per-package "Use your own keys" switch (standalone package rows whose
  // integration molecule manages by default). PRESENTATIONAL: ON reveals the
  // credential rows (managed ones grow an override input); OFF collapses a
  // fully-managed set to the single managed line — it never deletes anything.
  // Absent entries derive from state: ON iff a user-set key exists (or a
  // required key is missing — the setup flow always leads with its inputs).
  const [ownKeysSwitch, setOwnKeysSwitch] = useState<Record<string, boolean>>({})
  // Env vars detected in the app's code + registry declarations (E-03) —
  // rendered as the "Needs attention" / "Detected in your app" top sections.
  const [detected, setDetected] = useState<DetectedEnvVar[]>([])
  // Tooling-only keys the report dropped (playwright/vite plumbing). Shown as a
  // single muted line so the omission is stated, not hidden.
  const [detectedHidden, setDetectedHidden] = useState(0)
  // Which explanatory groups the user has opened. Both start closed: they exist
  // to answer "where did the rest go?", not to be read.
  const [openGroups, setOpenGroups] = useState<Record<'optional' | 'platform', boolean>>({
    optional: false,
    platform: false,
  })
  // Drafts for the "Needs attention" inline set-value inputs — separate state
  // so the poll never clobbers in-progress typing (same rule as `values`).
  const [detectedDrafts, setDetectedDrafts] = useState<Record<string, string>>({})
  // Key currently saving from a "Needs attention" row (disables that row's Save).
  const [savingDetected, setSavingDetected] = useState<string | null>(null)
  /**
   * Which environment the panel is editing (E-06). Everything below the
   * selector — what counts as configured, what a Save writes, what a Remove
   * clears — is relative to this. 'all' is the shared set both environments
   * see, and stays the default because most values genuinely are shared.
   */
  const [scope, setScope] = useState<EnvScope>('all')

  // The scope selector renders in the MODAL HEADER as the shared segmented
  // control (usePanelModalTabs) — one tab idiom across every workspace modal.
  usePanelModalTabs({
    tabs: ENV_SCOPES.map((value: EnvScope) => ({
      id: value,
      label:
        value === 'all'
          ? t('environment.scope.all', undefined, { defaultValue: 'Shared' })
          : value === 'dev'
            ? t('environment.scope.dev', undefined, { defaultValue: 'Development' })
            : t('environment.scope.prod', undefined, { defaultValue: 'Production' }),
      molId: `environment-scope-${value}`,
    })),
    activeId: scope,
    onSelect: (id) => setScope(id as EnvScope),
    label: t('environment.scope.label', undefined, { defaultValue: 'Environment' }),
    molId: 'environment-scope-selector',
  })

  // Every read below is SCOPED to the selected environment. Without that, the
  // Production tab answered with whatever scope happened to hold a value: a key
  // saved only on Development rendered a green check, its mask, and a "Your
  // keys" chip on Production — for a deploy that would boot with no key at all.
  const loadSetup = useCallback(async () => {
    try {
      const res = await http.get<SetupResponse>(`/projects/${projectId}/setup?environment=${scope}`)
      setIntegrations(res.data?.integrations ?? [])
    } catch (error) {
      console.error('EnvironmentPanel: failed to load setup status', error)
    }
  }, [http, projectId, scope])

  const loadServices = useCallback(async () => {
    try {
      const res = await loadServicesState(http, projectId)
      if (res) setServices(res)
    } catch (error) {
      // Non-fatal: the catalog rows still render; broker controls fail closed
      // (no "Use molecule's keys" without a live brokerSupported flag).
      console.error('EnvironmentPanel: failed to load services state', error)
    }
  }, [http, projectId])

  const loadVault = useCallback(async () => {
    try {
      const res = await http.get<VaultResponse>(`/projects/${projectId}/vault`)
      setVault({ secrets: res.data?.secrets ?? [], count: res.data?.count ?? 0 })
      setVaultAccess('ok')
    } catch (error) {
      const status = (error as { status?: number }).status
      const data = (error as { response?: { data?: { requiresSignup?: boolean } } }).response?.data
      if (status === 403 && data?.requiresSignup === true) {
        // Guest session — an expected state, not an error (the vault holds
        // credentials, which need an account to own them).
        setVaultAccess('requiresSignup')
        return
      }
      if (status === 403) {
        setVaultAccess('forbidden')
        return
      }
      console.error('EnvironmentPanel: failed to load vault', error)
      setVaultAccess('error')
    }
  }, [http, projectId])

  const loadEnvCount = useCallback(async () => {
    try {
      const res = await http.get<EnvVarsResponse>(`/projects/${projectId}/env?environment=${scope}`)
      setEnvCount(res.data?.count ?? Object.keys(res.data?.envVars ?? {}).length)
    } catch (error) {
      console.error('EnvironmentPanel: failed to load env var count', error)
    }
  }, [http, projectId, scope])

  const loadDetected = useCallback(async () => {
    try {
      const res = await http.get<DetectedEnvResponse>(
        `/projects/${projectId}/env/detected?environment=${scope}`,
      )
      setDetected(Array.isArray(res.data?.vars) ? res.data.vars : [])
      setDetectedHidden(res.data?.hiddenCount ?? 0)
    } catch (error) {
      // Non-fatal: the rest of the panel renders without the detected sections.
      console.error('EnvironmentPanel: failed to load detected env vars', error)
    }
  }, [http, projectId, scope])

  const loadAll = useCallback(async () => {
    await Promise.all([loadSetup(), loadServices(), loadVault(), loadEnvCount(), loadDetected()])
    setLoaded(true)
  }, [loadSetup, loadServices, loadVault, loadEnvCount, loadDetected])

  useEffect(() => {
    void loadAll()
    // Live refresh: state can flip from OUTSIDE this panel — Synthase writing
    // env vars or installing packages mid-session, the backend auto-wiring the
    // molecule default (each GET /services triggers its package sync +
    // default-wiring reconcile — the poll IS the awareness mechanism). Drafts
    // live in separate state, so a refresh never clobbers in-progress input.
    // Hidden tabs skip the poll.
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') void loadAll()
    }, 10_000)
    return () => clearInterval(interval)
  }, [loadAll])

  // Live cross-client refresh: an env/vault/allowlist change made by another
  // member (or Synthase on another client's turn) lands immediately instead of
  // on the next poll tick. Drafts live separately, so this never clobbers typing.
  useEffect(() => onResourceInvalidated('env', () => void loadAll()), [loadAll])

  // ── Actions (every mutation refreshes all stores) ──────────────────────────

  const handleBroker = useCallback(
    async (entry: ServiceCatalogEntry, enable: boolean) => {
      setBusyCategory(entry.category)
      setActionError(null)
      try {
        const action = enable
          ? enableBrokerAction(projectId, entry.category)
          : disableBrokerAction(projectId, entry.category)
        await runVaultAction(http, action)
        await loadAll()
      } catch (error) {
        // Turning the hosted backend on writes molecule's gateway credential
        // into the SAME key the user's own credential uses, and the vault keeps
        // one value per key — so the server refuses rather than replacing a key
        // it cannot give back. Ask, then retry with the confirmation.
        if (errorKeyOf(error) === 'vault.error.brokerWouldReplaceOwnKey') {
          const message =
            (error as { response?: { data?: { error?: string } } }).response?.data?.error ??
            t('services.brokerReplaceOwnKey', undefined, {
              defaultValue:
                "Turning on Molecule's hosted backend replaces your saved key. Your key can't be recovered afterwards.",
            })
          if (!(await confirmDialog({ message, danger: true, molId: 'broker-replace-dialog' }))) {
            setBusyCategory(null)
            return
          }
          try {
            await runVaultAction(
              http,
              enableBrokerAction(projectId, entry.category, { replaceOwnKeys: true }),
            )
            await loadAll()
            return
          } catch (retryError) {
            console.error('EnvironmentPanel: confirmed broker action failed', retryError)
          }
        } else {
          console.error('EnvironmentPanel: broker action failed', error)
        }
        setActionError(t('services.actionError', undefined, { defaultValue: 'Action failed.' }))
      } finally {
        setBusyCategory(null)
      }
    },
    [http, projectId, loadAll, t],
  )

  /**
   * Stores one BYO key. Returns whether it stuck, so the caller only clears the
   * typed draft on success — clearing it unconditionally destroyed a pasted key
   * every time a save was refused.
   */
  const handlePutSecret = useCallback(
    async (entry: ServiceCatalogEntry, key: string, value: string): Promise<boolean> => {
      const trimmed = value.trim()
      if (!trimmed) return false
      setBusyCategory(entry.category)
      setActionError(null)
      try {
        await runVaultAction(http, putSecretAction(projectId, key, trimmed, scope))
        await loadAll()
        return true
      } catch (error) {
        console.error('EnvironmentPanel: put-secret action failed', error)
        // The vault's deliberate denials each have a next step; a save that was
        // REFUSED must not read as a save that failed.
        setActionError(
          requiresSignup(error)
            ? t('services.actionRequiresSignup', undefined, {
                defaultValue:
                  'Sign up free to save credentials — your keys are kept in your account.',
              })
            : isInsufficientRoleError(error)
              ? t('services.actionForbidden', undefined, {
                  defaultValue: 'Saving credentials needs an admin role on this project.',
                })
              : ((error as { response?: { data?: { error?: string } } }).response?.data?.error ??
                t('services.actionError', undefined, { defaultValue: 'Action failed.' })),
        )
        return false
      } finally {
        setBusyCategory(null)
      }
    },
    [http, projectId, loadAll, scope, t],
  )

  const handleDeleteSecret = useCallback(
    async (entry: ServiceCatalogEntry, key: string) => {
      setBusyCategory(entry.category)
      setActionError(null)
      try {
        await runVaultAction(
          http,
          deleteSecretAction(projectId, key, scope === 'all' ? undefined : scope),
        )
        await loadAll()
      } catch (error) {
        console.error('EnvironmentPanel: delete-secret action failed', error)
        setActionError(t('services.actionError', undefined, { defaultValue: 'Action failed.' }))
      } finally {
        setBusyCategory(null)
      }
    },
    [http, projectId, loadAll, scope, t],
  )

  const handleSaveSetup = useCallback(
    async (integration: SetupIntegration) => {
      setSavingSetup(true)
      try {
        const saved = await saveIntegrationSecrets(http, projectId, integration, values, t, scope)
        if (saved) {
          setValues((prev) => {
            const next = { ...prev }
            for (const key of saved) delete next[key]
            return next
          })
          await loadAll()
        }
      } finally {
        setSavingSetup(false)
      }
    },
    [http, projectId, values, loadAll, scope, t],
  )

  const setSecretValue = useCallback(
    (name: string, value: string) => setValues((prev) => ({ ...prev, [name]: value })),
    [],
  )
  const setDraft = useCallback(
    (key: string, value: string) => setDrafts((prev) => ({ ...prev, [key]: value })),
    [],
  )
  const setDetectedDraft = useCallback(
    (key: string, value: string) => setDetectedDrafts((prev) => ({ ...prev, [key]: value })),
    [],
  )

  /**
   * Saves an inline value for a detected-but-unset var via the SAME
   * `PUT /projects/:id/env` flow (and notification copy) the credential rows
   * use — no new editor, just the existing set-value affordance in place.
   */
  const handleSaveDetected = useCallback(
    async (key: string) => {
      const value = detectedDrafts[key]
      if (!value?.trim()) return
      setSavingDetected(key)
      try {
        const res = await http.put<EnvSaveResponse>(`/projects/${projectId}/env`, {
          envVars: { [key]: value },
          environment: scope,
        })
        notifyEnvSaveOutcome(t, scope, res.data)
        setDetectedDrafts((prev) => {
          const next = { ...prev }
          delete next[key]
          return next
        })
        await loadAll()
      } catch (error) {
        console.error('EnvironmentPanel: failed to save detected env var', error)
        emitNotification(
          t('workspace.setup.saveError', undefined, {
            defaultValue: 'Could not save the keys — check your access and try again.',
          }),
          'error',
        )
      } finally {
        setSavingDetected(null)
      }
    },
    [http, projectId, detectedDrafts, loadAll, scope, t],
  )

  // ── Derived view model ─────────────────────────────────────────────────────

  /**
   * The vault as the SELECTED environment sees it — shared values with that
   * environment's overrides applied. Every "is this configured?" chip and
   * masked value below reads from this, so switching to Production and finding
   * a service unconfigured is the truth, not a rendering artifact.
   */
  const scopedVault = useMemo<VaultResponse | null>(() => {
    if (!vault) return null
    const secrets = resolveSecretsForScope(vault.secrets, scope)
    return { secrets, count: secrets.length }
  }, [vault, scope])

  /** Keys that hold an override in the selected environment (never `'all'`). */
  const overriddenKeys = useMemo(() => {
    if (scope === 'all' || !vault) return new Set<string>()
    return new Set(vault.secrets.filter((s) => s.environment === scope).map((s) => s.key))
  }, [vault, scope])

  const catalogByCategory = useMemo(
    () => new Map(SERVICE_CATALOG.map((entry) => [entry.category, entry])),
    [],
  )
  const liveByCategory = useMemo(
    () => new Map((services?.services ?? []).map((s) => [s.category, s])),
    [services],
  )
  // Integrations declaring IDENTICAL secret sets collapse into one row first
  // (e.g. the JWT provider + the Authentication resource both declare the
  // same generated keypair — two rows would show the same keys twice).
  const dedupedIntegrations = useMemo(() => dedupeIntegrations(integrations), [integrations])
  // Setup integrations nested under their service category (merged rows) vs
  // standalone package rows (no service category maps).
  const nestedByCategory = useMemo(() => {
    const map = new Map<string, SetupIntegration[]>()
    for (const integration of dedupedIntegrations) {
      const category = integration.serviceCategory
      if (!category || !catalogByCategory.has(category)) continue
      const list = map.get(category) ?? []
      list.push(integration)
      map.set(category, list)
    }
    return map
  }, [dedupedIntegrations, catalogByCategory])
  const packageOnly = useMemo(
    () =>
      dedupedIntegrations.filter(
        (i) => !i.serviceCategory || !catalogByCategory.has(i.serviceCategory),
      ),
    [dedupedIntegrations, catalogByCategory],
  )
  // Detected-vars split (E-03), by the group the API computed. "Needs
  // attention" is the only list that claims the user has to do something, and
  // the toolbar badge counts the same group — so a var can never be urgent in
  // one place and fine in the other. The other two groups exist to answer
  // "where did the rest go?" and stay collapsed. "Detected in your app" lists
  // vars the user has stored a value for, deduped against keys an integration
  // secret row already renders — an already-OK var repeating twice with two
  // different treatments would only confuse.
  const integrationSecretKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const integration of dedupedIntegrations)
      for (const secret of integration.secrets) keys.add(secret.name)
    return keys
  }, [dedupedIntegrations])
  const detectedUnset = useMemo(() => detected.filter((v) => v.group === 'attention'), [detected])
  // Has a default in the code, or the declaring package says it is optional.
  const detectedOptional = useMemo(() => detected.filter((v) => v.group === 'optional'), [detected])
  // Molecule (or the container) provides the value.
  const detectedPlatform = useMemo(() => detected.filter((v) => v.group === 'platform'), [detected])
  const detectedSet = useMemo(
    () => detected.filter((v) => v.group === 'set' && !integrationSecretKeys.has(v.key)),
    [detected, integrationSecretKeys],
  )

  // "In your app" = live-present categories ∪ categories implied by an
  // installed setup integration (covers a failed/slow live fetch).
  const groups = useMemo(() => {
    const present = new Set<string>()
    for (const s of services?.services ?? []) if (s.present) present.add(s.category)
    for (const category of nestedByCategory.keys()) present.add(category)
    return splitCatalogByPresence(SERVICE_CATALOG, present)
  }, [services, nestedByCategory])
  const billing = services?.billing ?? null

  // Rows that are missing keys the user has to supply — a service row whose
  // nested secrets have required values missing, or a standalone package row
  // the setup status flags as needing setup. These are exactly the rows whose
  // chip reads "N keys needed" / "Needs keys", and they render at the TOP under
  // "Needs attention" rather than down in "In your app": that section is the
  // panel's one answer to "what do I have to do?", and a keys-needed row is
  // that, whatever else it also is. The predicates below are the SAME ones
  // `mergedChip`/`packageChip` warn on, so a row can never warn in one place
  // and read fine in the other.
  const attentionCategories = useMemo(() => {
    const set = new Set<string>()
    for (const entry of groups.inApp) {
      const nested = nestedByCategory.get(entry.category) ?? []
      const missing = nested
        .flatMap((i) => i.secrets)
        .some((s) => s.required && s.status === 'missing')
      if (missing) set.add(entry.category)
    }
    return set
  }, [groups, nestedByCategory])
  const attentionServices = useMemo(
    () => groups.inApp.filter((entry) => attentionCategories.has(entry.category)),
    [groups, attentionCategories],
  )
  const attentionPackages = useMemo(() => packageOnly.filter((i) => i.needsSetup), [packageOnly])
  // The same rows, removed from "In your app" — hoisted, never duplicated: two
  // copies of one row would share an accordion key (both bodies open at once)
  // and repeat the same automation id twice.
  const inAppServices = useMemo(
    () => groups.inApp.filter((entry) => !attentionCategories.has(entry.category)),
    [groups, attentionCategories],
  )
  const inAppPackages = useMemo(() => packageOnly.filter((i) => !i.needsSetup), [packageOnly])
  // Keys the hoisted rows already render an input for, so a required secret
  // does not appear twice in the SAME section — once as its integration's row
  // and once as a bare detected key (the dedupe "Detected in your app" already
  // does against integration secrets).
  const attentionRowKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const entry of attentionServices)
      for (const integration of nestedByCategory.get(entry.category) ?? [])
        for (const secret of integration.secrets) keys.add(secret.name)
    for (const integration of attentionPackages)
      for (const secret of integration.secrets) keys.add(secret.name)
    return keys
  }, [attentionServices, attentionPackages, nestedByCategory])
  const detectedAttention = useMemo(
    () => detectedUnset.filter((v) => !attentionRowKeys.has(v.key)),
    [detectedUnset, attentionRowKeys],
  )
  /** Whether anything at all needs the user — drives the section + its `first`. */
  const hasAttentionSection =
    attentionServices.length > 0 || attentionPackages.length > 0 || detectedAttention.length > 0

  // ── Chips ──────────────────────────────────────────────────────────────────

  /**
   * Chip for a merged in-app service row — the UNIFIED in-app precedence
   * shared verbatim with the Storage panel's rows (identical labels + tones):
   * required-missing keys warn first, then a user-set key ("Your keys"), then
   * brokered (service rows only), then the explicit opt-out ("Default off"),
   * and everything else molecule already handles → "Auto-configured".
   */
  const mergedChip = (state: ServiceState, nested: SetupIntegration[]) => {
    const secrets = nested.flatMap((i) => i.secrets)
    const missingCount = secrets.filter((s) => s.required && s.status === 'missing').length
    if (missingCount > 0) {
      return (
        <Chip tone="warning" molId="services-chip-warning">
          {t(
            'workspace.setup.status.needsSetupCount',
            { count: missingCount },
            { defaultValue: `${missingCount} key${missingCount === 1 ? '' : 's'} needed` },
          )}
        </Chip>
      )
    }
    // "Your keys": a BYO vault key, or a nested secret the user set themselves
    // (molecule's broker AND managed rows are molecule's, never the user's).
    const userKeySet = state.byoConfigured || secrets.some(isUserSetSecret)
    if (userKeySet) {
      return (
        <Chip tone="byo" molId="services-chip-byo">
          {t('environment.chip.yourKeys', undefined, { defaultValue: 'Your keys' })}
        </Chip>
      )
    }
    if (state.brokered) {
      const chip = statusChip('brokered', state.optedOut)
      return (
        <Chip tone="broker" molId="services-chip-broker">
          {t(chip.key, undefined, { defaultValue: chip.label })}
        </Chip>
      )
    }
    if (state.optedOut) {
      const chip = statusChip('unconfigured', true)
      return (
        <Chip tone="none" molId="services-chip-none">
          {t(chip.key, undefined, { defaultValue: chip.label })}
        </Chip>
      )
    }
    return (
      <Chip tone="managed" molId="services-chip-managed">
        {t('workspace.setup.status.managed', undefined, { defaultValue: 'Auto-configured' })}
      </Chip>
    )
  }

  /** Chip for a catalog-only "More services" row (existing statusChip idiom). */
  const catalogOnlyChip = (state: ServiceState) => {
    const chip = statusChip(state.mode, state.optedOut)
    return (
      <Chip tone={chip.tone} molId={`services-chip-${chip.tone}`}>
        {t(chip.key, undefined, { defaultValue: chip.label })}
      </Chip>
    )
  }

  /**
   * Chip for a standalone package row — the SAME unified in-app precedence as
   * {@link mergedChip} and the Storage panel's rows (identical labels + tones):
   * missing keys warn, a user-set key reads "Your keys", and everything
   * molecule already handles reads "Auto-configured".
   */
  const packageChip = (integration: SetupIntegration) => {
    const shortId = integration.package.replace('@molecule/', '')
    const missingCount = integration.secrets.filter(
      (s) => s.required && s.status === 'missing',
    ).length
    if (integration.needsSetup) {
      return (
        <Chip tone="warning" molId={`setup-status-${shortId}`}>
          {missingCount > 0
            ? t(
                'workspace.setup.status.needsSetupCount',
                { count: missingCount },
                { defaultValue: `${missingCount} key${missingCount === 1 ? '' : 's'} needed` },
              )
            : t('workspace.setup.status.needsSetup', undefined, { defaultValue: 'Needs keys' })}
        </Chip>
      )
    }
    if (integration.secrets.some(isUserSetSecret)) {
      return (
        <Chip tone="byo" molId={`setup-status-${shortId}`}>
          {t('environment.chip.yourKeys', undefined, { defaultValue: 'Your keys' })}
        </Chip>
      )
    }
    return (
      <Chip tone="managed" molId={`setup-status-${shortId}`}>
        {t('workspace.setup.status.managed', undefined, { defaultValue: 'Auto-configured' })}
      </Chip>
    )
  }

  // ── Rows ───────────────────────────────────────────────────────────────────

  const toggleRow = (key: string) => setExpanded((cur) => (cur === key ? null : key))

  const renderServiceRow = (entry: ServiceCatalogEntry, inApp: boolean) => {
    const live = liveByCategory.get(entry.category)
    const state = deriveServiceState(entry, scopedVault, live)
    const nested = inApp ? (nestedByCategory.get(entry.category) ?? []) : []
    const rowKey = `svc:${entry.category}`
    return (
      <EnvRow
        key={rowKey}
        molId={`services-row-${entry.category}`}
        icon={iconFor(entry.category)}
        label={t(`services.category.${entry.category}`, undefined, { defaultValue: entry.label })}
        chip={inApp ? mergedChip(state, nested) : catalogOnlyChip(state)}
        expanded={expanded === rowKey}
        onToggle={() => toggleRow(rowKey)}
      >
        <ServiceRowBody
          entry={entry}
          live={live}
          state={state}
          billing={billing}
          busy={busyCategory === entry.category}
          vaultLocked={vaultLocked}
          inApp={inApp}
          nested={nested}
          values={values}
          onValueChange={setSecretValue}
          savingSetup={savingSetup}
          onSaveSetup={handleSaveSetup}
          drafts={drafts}
          onDraftChange={setDraft}
          onBroker={handleBroker}
          onPutSecret={handlePutSecret}
          onDeleteSecret={handleDeleteSecret}
        />
      </EnvRow>
    )
  }

  const renderPackageRow = (integration: SetupIntegration) => {
    const shortId = integration.package.replace('@molecule/', '')
    const rowKey = `pkg:${integration.package}`
    // Molecule-managed rows (every secret provisioned/auto/broker/managed)
    // show NO per-secret rows — just the description + ONE managed line.
    const managed = allSecretsManaged(integration.secrets)
    // The "Use your own keys" switch (the service rows' settings-row idiom):
    // offered where molecule provides a managed default (a provisioned or
    // managed-vault secret) AND the integration has secrets a user could
    // supply themselves (auto-generated-only sets have no user alternative).
    const hasManagedDefault = integration.secrets.some(
      (s) => s.status === 'provisioned' || s.vaultSource === 'managed',
    )
    const hasUserSuppliable = integration.secrets.some((s) => s.status !== 'auto')
    const showOwnSwitch = hasManagedDefault && hasUserSuppliable
    const userKeySet = integration.secrets.some(isUserSetSecret)
    const hasMissingRequired = integration.secrets.some((s) => s.required && s.status === 'missing')
    // PRESENTATIONAL: ON iff the user toggled it on, or (untouched) a user-set
    // key exists / required keys are missing — the setup flow always leads
    // with its inputs. Turning it OFF never deletes anything.
    const ownOn =
      showOwnSwitch && (ownKeysSwitch[integration.package] ?? (userKeySet || hasMissingRequired))
    // Secret rows hide ONLY while molecule fully manages the set and the
    // switch is off (or absent): a user-set key keeps them visible even when
    // toggled off — removing those keys IS the way back to the managed
    // defaults ("Remove your keys below…"), so they must stay reachable.
    const showSecrets = ownOn || !managed
    return (
      <EnvRow
        key={rowKey}
        molId={`setup-service-${shortId}`}
        icon={iconFor(integration.category)}
        label={integration.label ?? integration.package}
        packageName={[integration.package, ...(integration.absorbedPackages ?? [])].join(', ')}
        chip={packageChip(integration)}
        expanded={expanded === rowKey}
        onToggle={() => toggleRow(rowKey)}
      >
        {/* 1 — description */}
        {integration.description && (
          <div className={cm.textMuted} style={{ fontSize: 12, marginBottom: 10 }}>
            {integration.description}
          </div>
        )}
        {/* 2+3 — one SETTINGS ROW: the explanatory line (left) and the switch
            (right) share a line, matching the service rows' idiom. Without a
            switch: ONE explanatory muted line (never a box, never stacked). */}
        {showOwnSwitch ? (
          <Flex align="center" justify="between" gap="sm" style={{ marginBottom: 10 }}>
            <MutedNote molId={`setup-managed-${shortId}`} style={{ margin: 0, flex: 1 }}>
              {ownOn
                ? t('environment.ownKeysHint', undefined, {
                    defaultValue: "Using your own keys — Molecule's managed default is on standby.",
                  })
                : userKeySet
                  ? t('environment.removeKeysHint', undefined, {
                      defaultValue:
                        "Remove your keys below to fall back to Molecule's managed defaults.",
                    })
                  : t('environment.managedNote', undefined, {
                      defaultValue:
                        'Managed by Molecule — configured automatically, nothing to do.',
                    })}
            </MutedNote>
            <Switch
              data-mol-id={`setup-own-switch-${shortId}`}
              label={t('services.useYourOwnKeys', undefined, { defaultValue: 'Use your own keys' })}
              checked={ownOn}
              size="sm"
              onChange={() =>
                setOwnKeysSwitch((prev) => ({ ...prev, [integration.package]: !ownOn }))
              }
            />
          </Flex>
        ) : managed ? (
          <MutedNote molId={`setup-managed-${shortId}`}>
            {t('environment.managedNote', undefined, {
              defaultValue: 'Managed by Molecule — configured automatically, nothing to do.',
            })}
          </MutedNote>
        ) : (
          CAPTURED_CATEGORIES.has(integration.category ?? '') && (
            <MutedNote molId="environment-capture-note">
              {t('environment.captureNote', undefined, {
                defaultValue:
                  'In development, Molecule captures these automatically — see the Activity panel. Keys are only needed for real delivery in production.',
              })}
            </MutedNote>
          )
        )}
        {/* 4 — credential content (fully-managed rows collapse behind the
            OFF switch; ON reveals them with own-key override inputs) */}
        {showSecrets && (
          <IntegrationSecrets
            integration={integration}
            values={values}
            onValueChange={setSecretValue}
            saving={savingSetup}
            onSave={handleSaveSetup}
            showHeading={false}
            override={ownOn}
            locked={vaultLocked}
          />
        )}
      </EnvRow>
    )
  }

  /**
   * The one-line explanation a detected row carries. Every var in the panel now
   * says WHY it is where it is — the old list said only "Not set", which for a
   * var molecule sets on every boot read as "your app is broken".
   *
   * @param v - The detected var.
   * @returns The explanation, or null when the row's files line says enough.
   */
  const detectedExplanation = (v: DetectedEnvVar): string | null => {
    if (v.group === 'platform') {
      switch (v.platformKind) {
        case 'generated':
          return t('environment.detected.platform.generated', undefined, {
            defaultValue: 'molecule generates this for your project.',
          })
        case 'egress':
          return t('environment.detected.platform.egress', undefined, {
            defaultValue: 'molecule sets this so your app can reach the internet.',
          })
        case 'dev-server':
          return t('environment.detected.platform.devServer', undefined, {
            defaultValue: 'molecule sets this when it starts your app.',
          })
        case 'database':
          return t('environment.detected.platform.database', undefined, {
            defaultValue: 'molecule points this at your project database.',
          })
        case 'sidecar':
          return t('environment.detected.platform.sidecar', undefined, {
            defaultValue: 'molecule points this at a service it runs for you.',
          })
        case 'container':
          return t('environment.detected.platform.container', undefined, {
            defaultValue: 'The container your app runs in provides this.',
          })
        default:
          return t('environment.detected.platform.generic', undefined, {
            defaultValue: 'molecule provides this. You do not need to set it.',
          })
      }
    }
    if (v.group === 'optional') {
      if (v.defaultValue !== undefined) {
        return t(
          'environment.detected.fallsBackTo',
          { value: v.defaultValue },
          {
            defaultValue: `Falls back to ${v.defaultValue} when it is not set.`,
          },
        )
      }
      if (v.hasDefault) {
        return t('environment.detected.hasDefault', undefined, {
          defaultValue: 'The code already handles this being unset.',
        })
      }
      return t('environment.detected.optionalIntegration', undefined, {
        defaultValue: 'Optional. The integration works without it.',
      })
    }
    if (v.group === 'attention') {
      if (v.comment) return v.comment
      if (v.instructions) return v.instructions
      if (v.source === 'registry') {
        return t('environment.detected.registryNote', undefined, {
          defaultValue: 'Needed by an installed integration.',
        })
      }
      return t('environment.detected.noDefault', undefined, {
        defaultValue: 'No default in the code. Set it, or ask Synthase what it is for.',
      })
    }
    if (v.group === 'set') {
      // The floor that guarantees a 'set' row's expansion is never EMPTY: a
      // value whose only reads live in build config (e.g. BASE_PATH, read in
      // vite.config.ts — tooling by the scanner's classification) carries no
      // `files` and no setupUrl, so without this sentence the toggle opens to
      // a blank panel (owner report 2026-09-10).
      return t('environment.detected.setNote', undefined, {
        defaultValue: 'Already set. Your app reads this value when it builds or runs.',
      })
    }
    return null
  }

  /**
   * One detected-var row (E-03): monospace-style key label, a chip naming its
   * group, the `path:line` where the app reads it, one sentence explaining it,
   * and — only for vars the user can actually supply — the panel's inline
   * set-value affordance (password input + Save via `PUT /env`).
   *
   * A platform row deliberately has no input: offering one for a value molecule
   * overwrites on every boot is the same lie the old "Not set" chip told.
   *
   * @param v - The detected var.
   * @returns The rendered row.
   */
  const renderDetectedRow = (v: DetectedEnvVar) => {
    const rowKey = `detected:${v.key}`
    const draft = detectedDrafts[v.key] ?? ''
    const explanation = detectedExplanation(v)
    const settable = v.group === 'attention' || v.group === 'optional'
    const chip =
      v.group === 'attention' ? (
        <Chip tone="warning" molId={`env-detected-chip-${v.key}`}>
          {t('environment.chip.notSet', undefined, { defaultValue: 'Not set' })}
        </Chip>
      ) : v.group === 'optional' ? (
        <Chip tone="none" molId={`env-detected-chip-${v.key}`}>
          {t('environment.chip.optional', undefined, { defaultValue: 'Optional' })}
        </Chip>
      ) : v.group === 'platform' ? (
        <Chip tone="configured" molId={`env-detected-chip-${v.key}`}>
          {t('environment.chip.provided', undefined, { defaultValue: 'Provided' })}
        </Chip>
      ) : (
        <Chip tone="configured" molId={`env-detected-chip-${v.key}`}>
          {t('environment.chip.set', undefined, { defaultValue: 'Set' })}
        </Chip>
      )
    return (
      <EnvRow
        key={rowKey}
        molId={`env-detected-${v.key}`}
        icon={v.group === 'attention' ? 'key' : 'check-circle'}
        label={v.label ? `${v.key} — ${v.label}` : v.key}
        chip={chip}
        expanded={expanded === rowKey}
        onToggle={() => toggleRow(rowKey)}
      >
        {v.files.length > 0 && (
          <div
            data-mol-id={`env-detected-files-${v.key}`}
            className={cm.textMuted}
            style={{ fontSize: 11, lineHeight: 1.5, marginBottom: 6 }}
          >
            {t('environment.detected.usedIn', undefined, { defaultValue: 'Used in:' })}{' '}
            <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {v.files.join(', ')}
            </span>
          </div>
        )}
        {explanation && (
          <MutedNote
            molId={`env-detected-source-${v.key}`}
            style={{ marginBottom: settable ? 10 : 0 }}
          >
            {explanation}
          </MutedNote>
        )}
        {v.setupUrl && (
          <div style={{ marginBottom: 10 }}>
            <a
              href={v.setupUrl}
              target="_blank"
              rel="noreferrer noopener"
              data-mol-id={`env-detected-setup-url-${v.key}`}
              className={cm.textPrimary}
              style={{ fontSize: 12 }}
            >
              {t('services.getKey', undefined, { defaultValue: 'Get your key' })}
            </a>
          </div>
        )}
        {settable && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type={isSensitiveKey(v.key) ? 'password' : 'text'}
              data-mol-id={`env-detected-input-${v.key}`}
              value={draft}
              disabled={vaultLocked}
              onChange={(e) => setDetectedDraft(v.key, e.target.value)}
              placeholder={t('services.keyPlaceholder', undefined, {
                defaultValue: 'Paste key',
              })}
              autoComplete="off"
              style={{
                flex: 1,
                minWidth: 0,
                height: isMobile ? 40 : 32,
                boxSizing: 'border-box',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: inputFontSize,
                fontFamily: 'monospace',
                color: 'var(--mol-color-text, #e0e0e0)',
                outline: 'none',
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && draft.trim() && savingDetected !== v.key) {
                  void handleSaveDetected(v.key)
                }
              }}
            />
            <Button
              size="sm"
              variant="outline"
              data-mol-id={`env-detected-save-${v.key}`}
              disabled={savingDetected === v.key || vaultLocked || !draft.trim()}
              onClick={() => handleSaveDetected(v.key)}
              // Same height as the key input beside it at both breakpoints.
              style={{ height: isMobile ? 40 : 32 }}
            >
              {t('services.save', undefined, { defaultValue: 'Save' })}
            </Button>
          </div>
        )}
      </EnvRow>
    )
  }

  /**
   * A collapsed explanatory group ("Optional", "Set by molecule"). Closed by
   * default: these exist so the panel can account for every var it found without
   * putting any of them in front of the user as work to do.
   *
   * @param id - Group id (also the automation id suffix).
   * @param title - Heading text.
   * @param vars - The group's vars.
   * @param first - Whether it leads the panel (no top margin).
   * @returns The rendered group, or null when empty.
   */
  const renderDetectedGroup = (
    id: 'optional' | 'platform',
    title: string,
    vars: DetectedEnvVar[],
    first: boolean,
  ) => {
    if (vars.length === 0) return null
    const open = openGroups[id]
    return (
      <>
        {/* mol-bespoke-button: a GROUP HEADING that happens to toggle — the
            uppercase muted label + chevron that titles each env-var group.
            It must read as a heading, not as a button. */}
        <button
          type="button"
          data-mol-id={`env-group-${id}-toggle`}
          onClick={() => setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }))}
          className={cm.cn(cm.textMuted, cm.touchTargetCompact)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            width: '100%',
            padding: 0,
            margin: first ? '0 0 8px' : '16px 0 8px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
          aria-expanded={open}
        >
          <Icon name={open ? 'chevron-up' : 'chevron-down'} size={12} />
          {title} ({vars.length})
        </button>
        {open && vars.map(renderDetectedRow)}
      </>
    )
  }

  const hasDetectedSections =
    hasAttentionSection ||
    detectedSet.length > 0 ||
    detectedOptional.length > 0 ||
    detectedPlatform.length > 0 ||
    detectedHidden > 0

  return (
    <div data-mol-id="environment-panel" style={{ padding: 16, fontSize: 13 }}>
      {/* Panel-level status notices (guest / role / load / action errors)
          lead the contents — a state banner belongs above explanatory copy
          like the scope note. */}
      {vaultAccess === 'requiresSignup' && (
        <PanelNotice
          tone="primary"
          molId="environment-guest-note"
          action={
            // A real anchor (styled via cm.button to match the solid primary
            // sm Button exactly) so AuthModalMount's click interceptor opens
            // the in-IDE auth modal — programmatic navigate() escaped it.
            <a
              href="/signup"
              data-mol-id="environment-guest-signup"
              className={cm.button({ variant: 'solid', color: 'primary', size: 'sm' })}
              style={{ textDecoration: 'none' }}
            >
              {t('environment.guestSignup', undefined, { defaultValue: 'Sign up free' })}
            </a>
          }
        >
          {t('environment.guestNote', undefined, {
            defaultValue:
              "You're browsing as a guest — credential statuses are hidden. Sign up (free) to manage keys and use Molecule's hosted backend.",
          })}
        </PanelNotice>
      )}
      {vaultAccess === 'forbidden' && (
        <PanelNotice tone="info" molId="environment-forbidden-note">
          {t('environment.adminOnlyNote', undefined, {
            defaultValue: 'Credential management requires an admin role on this project.',
          })}
        </PanelNotice>
      )}
      {vaultAccess === 'error' && (
        <PanelNotice tone="error" molId="services-error">
          {t('environment.vaultLoadError', undefined, {
            defaultValue: "Couldn't load credential status — retrying shortly.",
          })}
        </PanelNotice>
      )}
      {actionError && (
        <PanelNotice tone="error" molId="services-action-error">
          {actionError}
        </PanelNotice>
      )}

      {/* E-06: which environment these values apply to. The Shared /
          Development / Production selector lives in the MODAL HEADER (the
          usePanelModalTabs call above); this note explains the active scope.
          Shared is the default because most values genuinely are; Development
          and Production show what that environment resolves to and write
          overrides that the other environment never sees. */}
      <MutedNote molId="environment-scope-note" style={{ marginBottom: 6 }}>
        {scope === 'all'
          ? t('environment.scope.noteAll', undefined, {
              defaultValue: 'Used by both environments unless overridden below.',
            })
          : scope === 'dev'
            ? t('environment.scope.noteDev', undefined, {
                defaultValue: 'Values here apply to your sandbox only. Production never sees them.',
              })
            : t('environment.scope.noteProd', undefined, {
                defaultValue:
                  'Values here apply to deployed apps only. Your sandbox never sees them.',
              })}
      </MutedNote>
      {scope !== 'all' && (
        <MutedNote molId="environment-scope-inherit-note" style={{ marginBottom: 10 }}>
          {overriddenKeys.size === 0
            ? t('environment.scope.inherit', undefined, {
                defaultValue:
                  'Shared values show here too. Saving one replaces it for this environment.',
              })
            : // Naming the overridden keys is the whole reason to switch scopes:
              // it is the only place the difference between the two environments
              // is visible at a glance.
              t(
                'environment.scope.overrides',
                { keys: [...overriddenKeys].sort().join(', ') },
                {
                  defaultValue: `Overridden here: ${[...overriddenKeys].sort().join(', ')}. Everything else uses the shared value.`,
                },
              )}
        </MutedNote>
      )}

      {!loaded ? (
        <Flex align="center" justify="center" className={cm.sp('py', 6)}>
          <MoleculeSpinner />
        </Flex>
      ) : (
        <>
          {/* Section 0 (E-03, top of the list) — everything the user has to
              act on: services and packages still missing keys (hoisted out of
              "In your app", where a "2 keys needed" chip was easy to scroll
              past), then env vars detected in the app's code + registry
              declarations that nothing above already asks for. The two
              explanatory groups below are collapsed, and keys read only by
              tests/build config never arrive here at all. */}
          {hasAttentionSection && (
            <>
              <SectionHeading molId="env-group-needs-attention" first>
                {t('environment.group.needsAttention', undefined, {
                  defaultValue: 'Needs attention',
                })}
              </SectionHeading>
              {attentionServices.map((entry) => renderServiceRow(entry, true))}
              {attentionPackages.map((integration) => renderPackageRow(integration))}
              {detectedAttention.map(renderDetectedRow)}
            </>
          )}
          {detectedSet.length > 0 && (
            <>
              <SectionHeading molId="env-group-detected" first={!hasAttentionSection}>
                {t('environment.group.detected', undefined, {
                  defaultValue: 'Detected in your app',
                })}
              </SectionHeading>
              {detectedSet.map(renderDetectedRow)}
            </>
          )}
          {renderDetectedGroup(
            'optional',
            t('environment.group.optional', undefined, { defaultValue: 'Optional' }),
            detectedOptional,
            !hasAttentionSection && detectedSet.length === 0,
          )}
          {renderDetectedGroup(
            'platform',
            t('environment.group.platform', undefined, { defaultValue: 'Set by molecule' }),
            detectedPlatform,
            !hasAttentionSection && detectedSet.length === 0 && detectedOptional.length === 0,
          )}
          {detectedHidden > 0 && (
            <MutedNote molId="env-detected-hidden" style={{ marginTop: 8 }}>
              {t(
                'environment.detected.hidden',
                { count: detectedHidden },
                {
                  defaultValue: `${detectedHidden} more are used only by tests and build config.`,
                },
              )}
            </MutedNote>
          )}

          {/* Section 1 — services + credentialed packages the app actually
              uses, minus the ones hoisted into "Needs attention" above. */}
          {(inAppServices.length > 0 || inAppPackages.length > 0) && (
            <>
              <SectionHeading molId="services-group-in-your-app" first={!hasDetectedSections}>
                {t('services.group.inYourApp', undefined, { defaultValue: 'In your app' })}
              </SectionHeading>
              {inAppServices.map((entry) => renderServiceRow(entry, true))}
              {inAppPackages.map((integration) => renderPackageRow(integration))}
            </>
          )}

          {/* Section 2 — what the app is allowed to reach on the network.
              Above the catalog on purpose: it is about THIS app (and it is
              where a user lands when their app can't reach a vendor API), so
              it must not sit below the long discovery list. */}
          <NetworkAccessSection
            canEdit={canEdit}
            projectId={projectId}
            expandedKey={expanded}
            onToggle={toggleRow}
            first={inAppServices.length === 0 && inAppPackages.length === 0 && !hasDetectedSections}
          />

          {/* Section 3 — the rest of the catalog (the discovery surface) */}
          {groups.more.length > 0 && (
            <>
              {/* Never `first`: the network section above always renders. */}
              <SectionHeading molId="services-group-more">
                {t('services.group.more', undefined, { defaultValue: 'More services' })}
              </SectionHeading>
              {groups.more.map((entry) => renderServiceRow(entry, false))}
            </>
          )}

          {/* Section 4 — raw custom environment variables */}
          <div style={{ marginTop: 16 }}>
            <EnvRow
              molId="env-section-variables"
              icon="code"
              label={t('environment.section.variables', undefined, {
                defaultValue: 'Custom environment variables',
              })}
              chip={
                envCount > 0 ? (
                  <Chip tone="managed" molId="env-vars-count">
                    {t(
                      'environment.chip.varCount',
                      { count: envCount },
                      { defaultValue: `${envCount} variable${envCount === 1 ? '' : 's'}` },
                    )}
                  </Chip>
                ) : null
              }
              expanded={expanded === 'vars'}
              onToggle={() => toggleRow('vars')}
            >
              <MutedNote molId="env-variables-note">
                {t('environment.variablesNote', undefined, {
                  defaultValue:
                    'Raw environment variables passed to your app (process.env). Most integrations configure themselves above — use this for your own custom config.',
                })}
              </MutedNote>
              <EnvVarEditor
                projectId={projectId}
                scope={scope}
                locked={vaultLocked}
                onChanged={loadAll}
              />
            </EnvRow>
          </div>
        </>
      )}
    </div>
  )
}
